import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  ViewChild,
  ViewContainerRef,
  ElementRef,
} from '@angular/core';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { Subject, takeUntil } from 'rxjs';
import { ConfigService } from '../../shared-services/config.service';
import { GlobalFilterService } from '../../shared-services/global-filter-service';
import { ChartRendererService } from '../../shared-services/chart-renderer.service';
import { ResponsiveLayoutService } from '../../shared-services/responsive-layout.service';
import { ThemeService } from '../../shared-services/theme.service';
import { DeepPartial, Theme } from '../../shared-services/theme.types';

type SortDirection = 'asc' | 'desc' | 'none';
type CellType =
  | 'text'
  | 'number'
  | 'input'
  | 'select'
  | 'textarea'
  | 'checkbox'
  | 'action'
  | 'chart'
  | 'link'
  | 'image'
  | 'serial';

type SuperOption = { label: string; value: any };
type SuperNavigation = {
  path?: string;
  url?: string;
  openInNewTab?: boolean;
  queryParams?: Record<string, any>;
  pathParams?: Record<string, any>;
};
type SuperChart = {
  type?: string;
  componentType?: string;
  chartType?: string;
  data?: any;
  widgetData?: any;
  series?: number[];
  value?: number;
  max?: number;
  color?: string;
  lineColor?: string;
  barColor?: string;
  height?: number;
  [key: string]: any;
};

const DEFAULT_SERIAL_COLUMN_LABEL = 'Serial N';
const DEFAULT_SERIAL_SELECTION_KEY = '_selected';
type SuperPopup = {
  type?: 'text' | 'table' | 'chart';
  title?: string;
  text?: string;
  sourcePath?: string;
  apiUrl?: string;
  method?: string;
  useQueryParams?: boolean;
  useQueryParam?: boolean;
  responsePath?: string;
  dataPath?: string;
  queryParams?: Record<string, any>;
  requestParams?: Record<string, any>;
  customPayload?: any;
  skipDefaultPayload?: boolean;
  table?: { columns?: Array<{ key: string; label?: string }>; rows?: any[] };
  chart?: SuperChart;
};
type SuperAction = {
  label: string;
  value?: any;
  className?: string;
  eventName?: string;
  apiUrl?: string;
  method?: string;
  queryParams?: Record<string, any>;
  requestParams?: Record<string, any>;
  useQueryParams?: boolean;
  useQueryParam?: boolean;
  responsePath?: string;
  dataPath?: string;
  customPayload?: any;
  skipDefaultPayload?: boolean;
  successMessage?: string;
  errorMessage?: string;
  disabled?: boolean;
  disabledKey?: string;
  showWhenKey?: string;
  navigate?: SuperNavigation;
};
type SuperControl = {
  key: string;
  label?: string;
  type?: 'select' | 'button' | 'link';
  className?: string;
  value?: any;
  defaultValue?: any;
  options?: any[];
  optionsApiUrl?: string;
  optionsMethod?: string;
  optionsResponsePath?: string;
  optionsLabelKey?: string;
  optionsValueKey?: string;
  optionsQueryParams?: Record<string, any>;
  optionsRequestParams?: Record<string, any>;
  optionsCustomPayload?: any;
  optionsSkipDefaultPayload?: boolean;
  optionsUseQueryParams?: boolean;
  reloadOptionsOnFilterChange?: boolean;
  filterKey?: string;
  asQueryParam?: boolean;
  filterListener?: string | string[];
  eventName?: string;
  navigate?: SuperNavigation;
};
type SuperColumn = {
  key: string;
  label?: string;
  type?: CellType;
  width?: string | number;
  minWidth?: string | number;
  align?: 'left' | 'center' | 'right';
  editable?: boolean;
  sortable?: boolean;
  hidden?: boolean;
  sticky?: 'left';
  selectionKey?: string;
  cascadeSelection?: boolean;
  placeholder?: string;
  inputType?: string;
  disabled?: boolean;
  trueLabel?: string;
  falseLabel?: string;
  options?: any[];
  optionsPath?: string;
  className?: string;
  headerClassName?: string;
  tooltip?: any;
  popup?: SuperPopup | string;
  chart?: SuperChart;
  actions?: SuperAction[];
  navigation?: SuperNavigation;
  drillDownToggle?: boolean;
};
type SuperColumnGroup = {
  key?: string;
  label?: string;
  columns?: string[];
  children?: SuperColumnGroup[];
  className?: string;
};
type SuperCellMergeRange = {
  rowIndex?: number;
  startRowIndex?: number;
  rowId?: string;
  startRowId?: string;
  rowKey?: string;
  rowValue?: any;
  columnIndex?: number;
  startColumnIndex?: number;
  columnKey?: string;
  startColumnKey?: string;
  rowSpan?: number;
  rowspan?: number;
  rows?: number;
  colSpan?: number;
  colspan?: number;
  columnSpan?: number;
  className?: string;
};
type SuperCellMergeRule = {
  columns?: string[];
  columnKey?: string;
  key?: string;
  direction?: 'vertical' | 'horizontal';
  matchBy?: string[];
  includeEmpty?: boolean;
  includeGroupRows?: boolean;
  matchDepth?: boolean;
  minSpan?: number;
  className?: string;
};
type SuperCellMergeConfig = {
  enabled?: boolean;
  ranges?: SuperCellMergeRange[];
  cells?: SuperCellMergeRange[];
  merges?: SuperCellMergeRange[];
  rules?: SuperCellMergeRule[];
};
type SuperConfig = {
  widgetId?: string;
  themeOverrides?: DeepPartial<Theme>;
  editable?: boolean;
  columns?: SuperColumn[];
  headerColumns?: SuperColumn[];
  columnGroups?: SuperColumnGroup[];
  headerGroups?: SuperColumnGroup[];
  rows?: any[];
  tableData?: any[];
  data?: any[];
  apiUrl?: string;
  mockDataUrl?: string;
  method?: string;
  rowsPath?: string;
  responseKey?: string;
  dataKey?: string;
  key?: string;
  tableHeight?: string | number;
  height?: string | number;
  rowHeight?: string | number;
  headerRowHeight?: string | number;
  emptyStateMessage?: string;
  errorMessage?: string;
  freezePane?: { topRows?: number; leftColumns?: number; topRowHeight?: string | number };
  defaultSort?: { key: string; direction?: 'asc' | 'desc' };
  sort?: { enabled?: boolean };
  drillDown?: {
    enabled?: boolean;
    childKey?: string;
    defaultExpandedLevel?: number;
    toggleColumnKey?: string;
    rowIdKey?: string;
    expandedKey?: string;
  };
  rowConfigKey?: string;
  cellConfigKey?: string;
  rowEditableKey?: string;
  rowEditableWhen?: any[];
  controls?: SuperControl[];
  tableControls?: SuperControl[];
  tableActions?: SuperAction[];
  eventNamespace?: string;
  editEventName?: string;
  controlEventName?: string;
  actionEventName?: string;
  selectionEventName?: string;
  dependsOnFilters?: string[];
  reloadOnAnyFilterChange?: boolean;
  cellMerges?: SuperCellMergeRange[] | SuperCellMergeConfig;
  mergeCells?: SuperCellMergeRange[] | SuperCellMergeConfig;
  bodyMerges?: SuperCellMergeRange[] | SuperCellMergeConfig;
  cellMergeRanges?: SuperCellMergeRange[];
  cellMergeRules?: SuperCellMergeRule[];
  autoMergeCells?: boolean | { columns?: string[]; includeEmpty?: boolean; includeGroupRows?: boolean; matchDepth?: boolean };
  rowGrouping?: {
    enabled?: boolean;
    groupBy?: Array<string | { key: string; label?: string; labelTemplate?: string; sort?: 'asc' | 'desc' }>;
    levels?: Array<string | { key: string; label?: string; labelTemplate?: string; sort?: 'asc' | 'desc' }>;
    labelColumnKey?: string;
    idPrefix?: string;
    groupRowConfig?: any;
  };
};
type RenderColumn = SuperColumn & {
  label: string;
  widthCss: string;
  minWidthCss: string;
  widthPx: number;
  minWidthPx: number;
  leftOffset: number;
};
type HeaderCellModel = {
  id: string;
  key?: string;
  label: string;
  className?: string;
  colspan: number;
  rowspan: number;
  depth: number;
  widthPx: number;
  minWidthPx: number;
  leftOffset: number;
  frozen: boolean;
  sortable: boolean;
  column?: RenderColumn;
  placeholder?: boolean;
};
type HeaderNodeModel = {
  id: string;
  label: string;
  className?: string;
  key?: string;
  column?: RenderColumn;
  children: HeaderNodeModel[];
};
type FlatRow = {
  id: string;
  depth: number;
  source: any;
  hasChildren: boolean;
  expanded: boolean;
};
type BodyCellSpan = {
  rowSpan: number;
  colSpan: number;
  className?: string;
};
type PopupState = {
  rowId: string;
  columnKey: string;
  title: string;
  type: 'text' | 'table' | 'chart';
  text: string;
  tableColumns: Array<{ key: string; label: string }>;
  tableRows: any[];
  chart: SuperChart | null;
  top: number;
  left: number;
  loading: boolean;
  error: string;
  requestId: number;
};
type DynamicChartTarget = {
  type: string;
  data: any;
};

@Component({
  selector: 'app-super-dynamic-chart-host',
  standalone: true,
  template: '',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SuperDynamicChartHostComponent implements OnChanges, OnDestroy {
  @Input() target: DynamicChartTarget | null = null;

  private renderedSignature = '';

  constructor(
    private viewContainerRef: ViewContainerRef,
    private chartRendererService: ChartRendererService,
  ) {}

  ngOnChanges(_: SimpleChanges): void {
    const target = this.target;
    if (!target?.type) {
      this.clearRenderedChart();
      return;
    }

    const signature = this.getTargetSignature(target);
    if (signature === this.renderedSignature) {
      return;
    }

    this.renderedSignature = signature;
    try {
      this.chartRendererService.renderChart(target.type, this.viewContainerRef, this.cloneObject(target.data));
    } catch (error) {
      this.clearRenderedChart();
      console.error('[ConfigDrivenSuperTable] dynamic chart render failed:', error, target);
    }
  }

  ngOnDestroy(): void {
    this.clearRenderedChart();
  }

  protected clearRenderedChart(): void {
    this.viewContainerRef.clear();
    this.renderedSignature = '';
  }

  protected getTargetSignature(target: DynamicChartTarget): string {
    try {
      return JSON.stringify({ type: target.type, data: target.data }) || target.type;
    } catch {
      return target.type;
    }
  }

  protected cloneObject<T>(value: T): T {
    if (Array.isArray(value)) return value.map((item) => this.cloneObject(item)) as T;
    if (value && typeof value === 'object') {
      return Object.keys(value as Record<string, any>).reduce((acc: any, key: string) => {
        acc[key] = this.cloneObject((value as Record<string, any>)[key]);
        return acc;
      }, {}) as T;
    }
    return value;
  }
}

@Component({
  selector: 'app-config-driven-super-table',
  standalone: true,
  imports: [CommonModule, MatProgressSpinnerModule, SuperDynamicChartHostComponent],
  templateUrl: './config-driven-super-table.component.html',
  styleUrl: './config-driven-super-table.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ConfigDrivenSuperTableComponent implements OnInit, AfterViewInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();
  private initialized = false;
  private filtersSubscribed = false;
  protected resolvedTheme: Theme | null = null;
  private usingDefaultSort = true;
  private _data: SuperConfig | null = null;

  @Input() set data(value: SuperConfig | null) {
    this._data = value;
    if (this.initialized) {
      this.applyResolvedTheme();
    }
    this.initializeComponent();
  }
  get data(): SuperConfig | null {
    return this._data;
  }

  defaultColumnWidth = 160;
  defaultChartColumnWidth = 260;
  defaultHeaderRowHeight = 50;
  defaultRowHeight = 46;

  loading = false;
  errorMessage = '';
  tableHeight = '58vh';
  tableMinWidth = '160px';
  headerRowHeight = this.defaultHeaderRowHeight;
  rowHeight = this.defaultRowHeight;
  frozenTopRowHeight = this.defaultRowHeight;

  rawRows: any[] = [];
  columns: SuperColumn[] = [];
  visibleColumns: RenderColumn[] = [];
  headerRows: HeaderCellModel[][] = [];
  flatRows: FlatRow[] = [];

  sortKey = '';
  sortDirection: SortDirection = 'none';

  controlValues: Record<string, any> = {};
  controlOptions: Record<string, SuperOption[]> = {};
  controlOptionsLoading: Record<string, boolean> = {};
  tableActionLoading: Record<string, boolean> = {};
  expandedState: Record<string, boolean> = {};
  selectedState: Record<string, boolean> = {};
  bodyCellSpans = new Map<string, BodyCellSpan>();
  coveredBodyCells = new Set<string>();
  activePopup: PopupState | null = null;
  currentFilters: any = { queryParams: {}, requestParams: {} };
  private popupRequestSequence = 0;
  private stickyOffsetScheduled = false;
  private measuredHeaderRowHeights: number[] = [];
  private measuredFrozenRowHeights: number[] = [];

  @ViewChild('tableRoot') tableRef?: ElementRef<HTMLTableElement>;

  constructor(
    protected configService: ConfigService,
    protected globalFilterService: GlobalFilterService,
    protected cdr: ChangeDetectorRef,
    protected responsiveLayoutService: ResponsiveLayoutService,
    protected themeService: ThemeService,
    protected hostElement: ElementRef<HTMLElement>,
  ) {}

  ngOnInit(): void {
    this.initialized = true;
    this.themeService.theme$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.applyResolvedTheme();
      this.queueStickyOffsetCalibration();
      this.cdr.markForCheck();
    });
    this.responsiveLayoutService.reflow$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.queueStickyOffsetCalibration();
      this.cdr.markForCheck();
    });
    this.initializeComponent();
  }

  ngAfterViewInit(): void {
    this.queueStickyOffsetCalibration();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get freezeTopRows(): number {
    return Math.max(0, this.freezePaneTopRows);
  }

  get freezePaneTopRows(): number {
    const raw = this.data?.freezePane?.topRows;
    if (raw === undefined || raw === null) return 0;
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? Math.floor(parsed) : 0;
  }

  get freezeHeader(): boolean {
    return this.freezePaneTopRows >= 0;
  }

  get freezeLeftColumns(): number {
    return Math.max(0, Number(this.data?.freezePane?.leftColumns || 0));
  }

  get controls(): SuperControl[] {
    const list = this.data?.controls || this.data?.tableControls || [];
    return Array.isArray(list) ? list : [];
  }

  get tableActions(): SuperAction[] {
    return Array.isArray(this.data?.tableActions) ? this.data!.tableActions! : [];
  }

  get childKey(): string {
    return this.data?.drillDown?.childKey || 'children';
  }

  get toggleColumnKey(): string {
    return this.data?.drillDown?.toggleColumnKey || '';
  }

  get emptyStateMessage(): string {
    return this.data?.emptyStateMessage || 'No data available.';
  }

  trackByColumn = (_: number, col: RenderColumn): string => col.key;
  trackByHeaderCell = (_: number, cell: HeaderCellModel): string => cell.id;
  trackByRow = (_: number, row: FlatRow): string => row.id;

  initializeComponent(): void {
    if (!this.initialized || !this.data) return;

    this.applyResolvedTheme();
    this.errorMessage = '';
    this.tableHeight = this.normalizeCssSize(this.data.tableHeight || this.data.height || '58vh');
    this.headerRowHeight = this.toPixels(this.data.headerRowHeight, this.defaultHeaderRowHeight);
    this.rowHeight = this.toPixels(this.data.rowHeight, this.defaultRowHeight);
    this.frozenTopRowHeight = this.toPixels(this.data.freezePane?.topRowHeight, this.rowHeight);

    this.currentFilters = this.globalFilterService.getCurrentFilters();
    this.initializeControlValues();
    this.subscribeToFilters();
    this.loadControlOptions(this.currentFilters, true);
    this.loadRows(this.currentFilters);
  }

  subscribeToFilters(): void {
    if (this.filtersSubscribed) return;
    this.filtersSubscribed = true;
    this.globalFilterService.filter$.pipe(takeUntil(this.destroy$)).subscribe((filters) => {
      this.currentFilters = filters || { queryParams: {}, requestParams: {} };
      this.loadControlOptions(this.currentFilters, false);
      if (this.usesRemoteData() && this.shouldReloadForFilters(filters)) {
        this.loadRows(this.currentFilters);
      }
    });
  }

  shouldReloadForFilters(filters: any): boolean {
    if (filters?.skipReload) return false;
    const dependsOn = Array.isArray(this.data?.dependsOnFilters) ? this.data!.dependsOnFilters! : [];
    const changed = Array.isArray(filters?.changedFilterKey)
      ? filters.changedFilterKey
      : filters?.changedFilterKey
        ? [filters.changedFilterKey]
        : [];
    if (dependsOn.length === 0) return this.data?.reloadOnAnyFilterChange === true;
    return changed.some((k: string) => dependsOn.includes(k));
  }

  loadRows(filters?: any): void {
    if (!this.data) return;

    this.columns = this.normalizeColumns();
    const inlineRows = this.extractInlineRows();
    if (inlineRows.length > 0) {
      this.rawRows = this.applyRowGrouping(this.cloneRows(inlineRows));
      this.loading = false;
      this.rebuildTableModel();
      return;
    }

    const url = this.data.apiUrl || this.data.mockDataUrl;
    if (!url) {
      this.rawRows = [];
      this.loading = false;
      this.rebuildTableModel();
      return;
    }

    this.loading = true;
    this.cdr.markForCheck();
    this.configService
      .getConfig(url, this.data, filters, this.resolveMethod(url), true)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          this.rawRows = this.applyRowGrouping(this.cloneRows(this.extractRows(res)));
          this.loading = false;
          this.errorMessage = '';
          this.rebuildTableModel();
        },
        error: () => {
          this.rawRows = [];
          this.loading = false;
          this.errorMessage = this.data?.errorMessage || 'Unable to load table data.';
          this.rebuildTableModel();
        },
      });
  }

  rebuildTableModel(): void {
    if (this.columns.length === 0) this.columns = this.normalizeColumns(this.rawRows);
    this.visibleColumns = this.buildVisibleColumns(this.columns);
    this.headerRows = this.buildHeaderRows(this.visibleColumns);
    this.tableMinWidth = `${Math.max(this.computeMinTableWidth(), this.defaultColumnWidth)}px`;
    this.measuredHeaderRowHeights = [];
    this.measuredFrozenRowHeights = [];

    const rowsForRender = this.sortRows(this.cloneRows(this.rawRows));
    this.flatRows = this.flattenRows(rowsForRender);
    this.rebuildBodyCellSpans();
    if (this.activePopup && !this.flatRows.some((r) => r.id === this.activePopup?.rowId)) {
      this.activePopup = null;
    }
    this.queueStickyOffsetCalibration();
    this.cdr.markForCheck();
  }

  protected applyResolvedTheme(): void {
    const resolvedTheme = this.themeService.resolve(this.data?.themeOverrides);
    if (!this.isResolvedTheme(resolvedTheme)) {
      this.resolvedTheme = null;
      return;
    }
    this.resolvedTheme = resolvedTheme;
    this.themeService.applyThemeTokensTo(resolvedTheme, this.hostElement.nativeElement);
    this.applySuperTableHeaderTokens(resolvedTheme);
  }

  protected isResolvedTheme(theme: Theme | null | undefined): theme is Theme {
    return !!theme?.layout && !!theme?.card && !!theme?.table && !!theme?.form && !!theme?.button && !!theme?.dialog;
  }

  protected applySuperTableHeaderTokens(theme: Theme): void {
    const tableOverrides = this.data?.themeOverrides?.table;
    const headerBg = tableOverrides?.headerBg || theme.card.headerBg || theme.table.headerBg || '#0f172a';
    const headerText = tableOverrides?.headerText || theme.card.headerText || theme.table.headerText || '#f8fafc';

    this.hostElement.nativeElement.style.setProperty('--super-table-header-bg', headerBg);
    this.hostElement.nativeElement.style.setProperty('--super-table-header-text', headerText);
  }
  initializeControlValues(): void {
    const next: Record<string, any> = {};
    for (const control of this.controls) {
      if (!control?.key) continue;
      if (this.controlValues[control.key] !== undefined) {
        next[control.key] = this.controlValues[control.key];
        continue;
      }
      if (control.defaultValue !== undefined) {
        next[control.key] = control.defaultValue;
        continue;
      }
      const options = this.resolveControlOptions(control);
      next[control.key] = options[0]?.value ?? control.value ?? '';
    }
    this.controlValues = next;
  }

  resolveControlOptions(control: SuperControl): SuperOption[] {
    if (!control?.key) return [];
    const dynamic = this.controlOptions[control.key];
    if (Array.isArray(dynamic) && dynamic.length > 0) return dynamic;
    return this.normalizeOptions(control.options);
  }

  loadControlOptions(filters: any, initialLoad: boolean): void {
    for (const control of this.controls) {
      if (!control?.key || !control.optionsApiUrl) continue;
      const shouldReload =
        initialLoad
        || control.reloadOptionsOnFilterChange === true
        || !Array.isArray(this.controlOptions[control.key])
        || this.controlOptions[control.key].length === 0;
      if (!shouldReload) continue;
      this.loadControlOptionsForControl(control, filters);
    }
  }

  loadControlOptionsForControl(control: SuperControl, filters: any): void {
    if (!control?.optionsApiUrl) return;
    const url = this.resolveFilterTemplate(String(control.optionsApiUrl), this.buildExternalContext());
    const requestConfig = this.buildControlOptionsRequestConfig(control);
    const mergedFilters = this.buildExternalFilters(
      control.optionsQueryParams,
      control.optionsRequestParams,
      filters,
      control.optionsUseQueryParams === true,
    );
    const method = control.optionsMethod || this.resolveMethod(url);
    this.controlOptionsLoading[control.key] = true;

    this.configService
      .getConfig(url, requestConfig, mergedFilters, method, true)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          const options = this.extractControlOptionsFromResponse(response, control);
          this.controlOptions[control.key] = options;
          this.controlOptionsLoading[control.key] = false;
          this.syncControlValueWithOptions(control);
          this.cdr.markForCheck();
        },
        error: () => {
          this.controlOptions[control.key] = this.normalizeOptions(control.options);
          this.controlOptionsLoading[control.key] = false;
          this.syncControlValueWithOptions(control);
          this.cdr.markForCheck();
        },
      });
  }

  buildControlOptionsRequestConfig(control: SuperControl): any {
    const request: any = {
      ...this.cloneObject(control || {}),
      customPayload: this.cloneObject(control.optionsCustomPayload || {}),
      skipDefaultPayload: control.optionsSkipDefaultPayload === true,
    };

    [
      'key',
      'label',
      'type',
      'className',
      'value',
      'defaultValue',
      'options',
      'optionsApiUrl',
      'optionsMethod',
      'optionsResponsePath',
      'optionsLabelKey',
      'optionsValueKey',
      'optionsQueryParams',
      'optionsRequestParams',
      'optionsCustomPayload',
      'optionsSkipDefaultPayload',
      'optionsUseQueryParams',
      'reloadOptionsOnFilterChange',
      'filterKey',
      'asQueryParam',
      'filterListener',
      'eventName',
      'navigate',
    ].forEach((key) => delete request[key]);

    request.widgetId = request.widgetId || `${this.data?.widgetId || 'config-driven-super-table'}-control-${control.key}`;
    return request;
  }

  extractControlOptionsFromResponse(response: any, control: SuperControl): SuperOption[] {
    const paths = [
      control.optionsResponsePath,
      'options',
      'data',
      'items',
      'result',
    ].filter(Boolean);

    let source: any = response;
    for (const path of paths) {
      const resolved = this.getValueByPath(response, String(path));
      if (Array.isArray(resolved)) {
        source = resolved;
        break;
      }
    }

    if (!Array.isArray(source)) {
      if (Array.isArray(response)) source = response;
      else source = [];
    }

    const labelKey = control.optionsLabelKey || 'label';
    const valueKey = control.optionsValueKey || 'value';

    return source
      .map((item: any) => {
        if (item && typeof item === 'object' && !Array.isArray(item)) {
          return {
            label: item[labelKey] ?? item.label ?? item.name ?? String(item[valueKey] ?? item.value ?? ''),
            value: item[valueKey] ?? item.value ?? item.id ?? item.key ?? item[labelKey] ?? item.label,
          };
        }
        return { label: String(item), value: item };
      })
      .filter((option: SuperOption) => option.value !== undefined && option.value !== null && option.value !== '');
  }

  syncControlValueWithOptions(control: SuperControl): void {
    const options = this.resolveControlOptions(control);
    if (options.length === 0) return;
    const existing = this.controlValues[control.key];
    const exists = options.some((option) => this.areValuesEqual(option.value, existing));
    if (exists) return;
    if (control.defaultValue !== undefined) {
      this.controlValues[control.key] = control.defaultValue;
      return;
    }
    this.controlValues[control.key] = options[0].value;
  }

  extractInlineRows(): any[] {
    const candidates = [this.data?.rows, this.data?.tableData, this.data?.data];
    const rows = candidates.find((item) => Array.isArray(item));
    return Array.isArray(rows) ? rows : [];
  }

  extractRows(response: any): any[] {
    const paths = [this.data?.rowsPath, this.data?.responseKey, this.data?.dataKey, this.data?.key].filter(Boolean);
    for (const path of paths) {
      const value = this.getValueByPath(response, path as string);
      if (Array.isArray(value)) return value;
    }
    if (Array.isArray(response)) return response;
    for (const path of ['rows', 'data', 'options', 'result', 'items']) {
      const value = this.getValueByPath(response, path);
      if (Array.isArray(value)) return value;
    }
    if (response && typeof response === 'object' && !Array.isArray(response) && this.looksLikeTableRow(response)) {
      return [response];
    }
    return [];
  }

  private looksLikeTableRow(response: any): boolean {
    const columns = Array.isArray(this.data?.columns) ? this.data.columns : [];
    if (!columns.length) return false;

    return columns.some((column: any) => {
      const key = String(column?.key || '').trim();
      return !!key && this.getValueByPath(response, key) !== undefined;
    });
  }

  applyRowGrouping(rows: any[]): any[] {
    if (!Array.isArray(rows) || rows.length === 0) return [];
    const levels = this.getRowGroupingLevels();
    if (levels.length === 0) return rows;
    return this.groupRowsByLevels(rows, levels, 0, '');
  }

  getRowGroupingLevels(): Array<{ key: string; label: string; labelTemplate?: string; sort?: 'asc' | 'desc' }> {
    if (!this.data?.rowGrouping?.enabled) return [];
    const source = this.data?.rowGrouping?.groupBy || this.data?.rowGrouping?.levels || [];
    if (!Array.isArray(source) || source.length === 0) return [];

    const levels: Array<{ key: string; label: string; labelTemplate?: string; sort?: 'asc' | 'desc' }> = [];
    for (const item of source) {
      if (typeof item === 'string') {
        const key = String(item).trim();
        if (!key) continue;
        levels.push({ key, label: this.humanizeKey(key), sort: 'asc' });
        continue;
      }
      if (!item || typeof item !== 'object') continue;
      const key = String(item.key || '').trim();
      if (!key) continue;
      levels.push({
        key,
        label: item.label || this.humanizeKey(key),
        labelTemplate: item.labelTemplate || '',
        sort: item.sort === 'desc' ? 'desc' : 'asc',
      });
    }
    return levels;
  }

  groupRowsByLevels(
    rows: any[],
    levels: Array<{ key: string; label: string; labelTemplate?: string; sort?: 'asc' | 'desc' }>,
    depth: number,
    parentPath: string,
  ): any[] {
    if (depth >= levels.length) return rows;
    const level = levels[depth];
    const buckets = new Map<string, { value: any; rows: any[] }>();

    for (const row of rows) {
      const raw = this.getValueByPath(row, level.key);
      const key = raw === undefined || raw === null || raw === '' ? '__ungrouped__' : String(raw);
      const existing = buckets.get(key);
      if (existing) {
        existing.rows.push(row);
      } else {
        buckets.set(key, { value: raw, rows: [row] });
      }
    }

    const entries = Array.from(buckets.entries()).sort((a, b) => {
      const left = a[1].value ?? '';
      const right = b[1].value ?? '';
      const result = String(left).localeCompare(String(right), undefined, { numeric: true, sensitivity: 'base' });
      return level.sort === 'desc' ? -result : result;
    });

    return entries.map(([bucketKey, bucket], index) => {
      const children = this.groupRowsByLevels(bucket.rows, levels, depth + 1, `${parentPath}-${index}`);
      const labelColumnKey = this.resolveRowGroupLabelColumnKey();
      const displayValue = bucket.value === undefined || bucket.value === null || bucket.value === '' ? 'Unspecified' : bucket.value;
      const label = this.buildRowGroupLabel(level, displayValue);
      const idKey = this.data?.drillDown?.rowIdKey || 'id';
      const rowCfgKey = this.getRowConfigKey();
      const groupRowConfig = this.cloneObject(this.data?.rowGrouping?.groupRowConfig || {});
      const rowConfig = {
        editable: false,
        className: [groupRowConfig?.className || '', `row-group row-group-depth-${depth}`].filter(Boolean).join(' '),
        ...groupRowConfig,
      };
      const groupCellValues = this.buildGroupRowCellValues(bucket.rows, labelColumnKey, level.key);

      return {
        [idKey]: `${this.data?.rowGrouping?.idPrefix || 'group'}-${depth}-${index}-${String(bucketKey).replace(/\s+/g, '-').toLowerCase()}`,
        ...groupCellValues,
        [labelColumnKey]: label,
        [level.key]: displayValue,
        [this.childKey]: children,
        [rowCfgKey]: rowConfig,
      };
    });
  }

  resolveRowGroupLabelColumnKey(): string {
    const configured = String(this.data?.rowGrouping?.labelColumnKey || '').trim();
    if (configured) return configured;
    if (this.toggleColumnKey) return this.toggleColumnKey;
    return this.visibleColumns[0]?.key || this.columns[0]?.key || 'group';
  }

  buildRowGroupLabel(level: { key: string; label: string; labelTemplate?: string }, value: any): string {
    const display = String(value ?? '');
    if (level.labelTemplate) {
      return String(level.labelTemplate)
        .replace(/\{label\}/g, level.label)
        .replace(/\{value\}/g, display)
        .replace(/\{key\}/g, level.key);
    }
    return `${level.label}: ${display}`;
  }

  buildGroupRowCellValues(sourceRows: any[], labelColumnKey: string, currentLevelKey: string): Record<string, any> {
    if (!Array.isArray(sourceRows) || sourceRows.length === 0) return {};
    const result: Record<string, any> = {};
    const columns = Array.isArray(this.columns) ? this.columns : [];
    for (const column of columns) {
      if (!column?.key) continue;
      const key = column.key;
      if (key === labelColumnKey || key === currentLevelKey || key === this.childKey) continue;
      const value = this.resolveGroupCellValue(column, sourceRows);
      if (value !== undefined) {
        result[key] = value;
      }
    }
    return result;
  }

  resolveGroupCellValue(column: SuperColumn, sourceRows: any[]): any {
    const values = sourceRows
      .map((row) => this.getValueByPath(row, column.key))
      .filter((value) => value !== undefined && value !== null && value !== '');
    if (values.length === 0) return undefined;

    const type = (column.type || 'text') as CellType;

    if (type === 'chart') {
      const sourceRow = sourceRows.find((row) => this.hasChartPayload(this.getValueByPath(row, column.key)));
      const raw = this.cloneObject(sourceRow ? this.getValueByPath(sourceRow, column.key) : values[0]);
      if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return raw;
      const resolvedType = sourceRow
        ? this.resolveChartType(sourceRow, this.cloneObject(column.chart || {}), raw)
        : '';
      if (resolvedType && raw.type === undefined && raw.componentType === undefined && raw.chartType === undefined) {
        raw.type = resolvedType;
      }
      return raw;
    }

    if (type === 'number' || values.every((value) => this.isNumeric(value))) {
      const nums = values.map((value) => Number(value)).filter((value) => Number.isFinite(value));
      if (nums.length > 0) return nums.reduce((sum, value) => sum + value, 0);
    }

    if (type === 'action') return [];

    if (type === 'checkbox') {
      return values.some((value) => {
        if (typeof value === 'boolean') return value;
        if (typeof value === 'number') return value !== 0;
        return String(value).toLowerCase() === 'true';
      });
    }

    if (type === 'image' || type === 'link') return this.cloneObject(values[0]);

    const unique = Array.from(new Set(values.map((value) => String(value))));
    if (unique.length === 1) return this.cloneObject(values[0]);
    return `${values.length} items`;
  }

  normalizeColumns(sourceRows: any[] = []): SuperColumn[] {
    const configured = Array.isArray(this.data?.columns)
      ? this.data?.columns
      : Array.isArray(this.data?.headerColumns)
        ? this.data?.headerColumns
        : [];

    if (configured.length > 0) return configured.map((c: any, i: number) => this.normalizeColumn(c, i));

    const sample = sourceRows[0] || {};
    return Object.keys(sample)
      .filter((k) => !this.getReservedKeys().includes(k) && k !== this.childKey)
      .map((key, i) => this.normalizeColumn({ key, label: this.humanizeKey(key) }, i));
  }

  normalizeColumn(column: any, index: number): SuperColumn {
    const key = column?.key || `column_${index + 1}`;
    const normalizedType = column?.type || 'text';
    const isChartColumn = normalizedType === 'chart' || !!column?.chart;
    const fallbackWidth = isChartColumn ? this.defaultChartColumnWidth : this.defaultColumnWidth;
    const base: SuperColumn = {
      key,
      label: column?.label || column?.header || column?.title || this.humanizeKey(key),
      type: normalizedType,
      width: column?.width || fallbackWidth,
      minWidth: column?.minWidth || column?.width || fallbackWidth,
      align: column?.align || 'left',
      editable: column?.editable ?? this.data?.editable ?? true,
      sortable: column?.sortable ?? true,
      hidden: column?.hidden ?? false,
      sticky: column?.sticky,
      selectionKey: column?.selectionKey,
      cascadeSelection: column?.cascadeSelection ?? false,
      placeholder: column?.placeholder || '',
      inputType: column?.inputType || '',
      disabled: column?.disabled ?? false,
      trueLabel: column?.trueLabel,
      falseLabel: column?.falseLabel,
      options: column?.options,
      optionsPath: column?.optionsPath,
      className: column?.className || '',
      headerClassName: column?.headerClassName || '',
      tooltip: column?.tooltip,
      popup: column?.popup,
      chart: column?.chart,
      actions: Array.isArray(column?.actions) ? column.actions : [],
      navigation: column?.navigation,
      drillDownToggle: column?.drillDownToggle ?? false,
    };

    if (normalizedType === 'serial') {
      return {
        ...base,
        label: column?.label || DEFAULT_SERIAL_COLUMN_LABEL,
        width: column?.width || 92,
        minWidth: column?.minWidth || column?.width || 92,
        sticky: column?.sticky ?? 'left',
        editable: false,
        sortable: false,
        disabled: true,
      };
    }

    return base;
  }

  buildVisibleColumns(columns: SuperColumn[]): RenderColumn[] {
    const visible = columns.filter((c) => !c.hidden);
    let left = 0;
    return visible.map((column, index) => {
      const widthPx = this.toPixels(column.width || column.minWidth, this.defaultColumnWidth);
      const minWidthPx = this.toPixels(column.minWidth || column.width, widthPx);
      const item: RenderColumn = {
        ...column,
        label: column.label || this.humanizeKey(column.key),
        widthCss: this.normalizeCssSize(widthPx),
        minWidthCss: this.normalizeCssSize(minWidthPx),
        widthPx,
        minWidthPx,
        leftOffset: this.snapStickyCoordinate(left),
      };
      if (this.isFrozenColumn(index, column)) left = this.snapStickyCoordinate(left + widthPx);
      return item;
    });
  }

  computeMinTableWidth(): number {
    return this.visibleColumns.reduce((sum, c) => sum + c.widthPx, 0);
  }

  getConfiguredColumnGroups(): SuperColumnGroup[] {
    const groups = this.data?.columnGroups || this.data?.headerGroups || [];
    return Array.isArray(groups) ? groups : [];
  }

  buildHeaderRows(visibleColumns: RenderColumn[]): HeaderCellModel[][] {
    if (!Array.isArray(visibleColumns) || visibleColumns.length === 0) return [];

    const columnsByKey = new Map<string, RenderColumn>();
    const frozenByKey = new Map<string, boolean>();
    visibleColumns.forEach((column, index) => {
      columnsByKey.set(column.key, column);
      frozenByKey.set(column.key, this.isFrozenColumn(index, column));
    });

    const consumed = new Set<string>();
    const groupedRoots: HeaderNodeModel[] = [];
    const configuredGroups = this.getConfiguredColumnGroups();

    configuredGroups.forEach((group, index) => {
      const node = this.buildHeaderNodeFromGroup(group, `group-${index}`, columnsByKey, consumed);
      if (node) groupedRoots.push(node);
    });

    const groupedRootByFirstColumn = new Map<string, HeaderNodeModel>();
    groupedRoots.forEach((node) => {
      const firstLeaf = this.collectHeaderLeafColumns(node)[0];
      if (firstLeaf) groupedRootByFirstColumn.set(firstLeaf.key, node);
    });

    const roots: HeaderNodeModel[] = [];
    visibleColumns.forEach((column) => {
      const groupedRoot = groupedRootByFirstColumn.get(column.key);
      if (groupedRoot) {
        roots.push(groupedRoot);
        return;
      }
      if (consumed.has(column.key)) return;
      roots.push(this.createHeaderLeafNode(column, `leaf-${column.key}`));
    });

    if (roots.length === 0) return [];

    const maxDepth = Math.max(...roots.map((node) => this.getHeaderDepth(node)));
    const rows: HeaderCellModel[][] = Array.from({ length: maxDepth }, () => []);
    roots.forEach((node, index) => {
      this.appendHeaderCells(node, 0, maxDepth, rows, frozenByKey, `root-${index}`);
    });

    return rows;
  }

  buildHeaderNodeFromGroup(
    group: SuperColumnGroup,
    nodeId: string,
    columnsByKey: Map<string, RenderColumn>,
    consumed: Set<string>,
  ): HeaderNodeModel | null {
    if (!group || typeof group !== 'object') return null;

    const children: HeaderNodeModel[] = [];
    const addLeaf = (keyValue: any, leafIdSuffix: string): void => {
      const key = String(keyValue || '').trim();
      if (!key || consumed.has(key)) return;
      const column = columnsByKey.get(key);
      if (!column) return;
      consumed.add(key);
      const leaf = this.createHeaderLeafNode(column, `${nodeId}-${leafIdSuffix}`);
      if (group.className) {
        leaf.className = [leaf.className || '', group.className].filter(Boolean).join(' ');
      }
      children.push(leaf);
    };

    if (Array.isArray(group.columns)) {
      group.columns.forEach((columnKey, index) => addLeaf(columnKey, `col-${index}`));
    }

    if (group.key && !Array.isArray(group.children) && !Array.isArray(group.columns)) {
      addLeaf(group.key, 'key');
    }

    if (Array.isArray(group.children)) {
      group.children.forEach((child, index) => {
        const childNode = this.buildHeaderNodeFromGroup(child, `${nodeId}-child-${index}`, columnsByKey, consumed);
        if (childNode) children.push(childNode);
      });
    }

    if (children.length === 0) return null;

    if (children.length === 1 && !group.label) {
      return children[0];
    }

    return {
      id: nodeId,
      key: group.key,
      label: group.label || this.humanizeKey(group.key || nodeId),
      className: group.className || '',
      children,
    };
  }

  createHeaderLeafNode(column: RenderColumn, nodeId: string): HeaderNodeModel {
    return {
      id: nodeId,
      key: column.key,
      label: column.label || this.humanizeKey(column.key),
      className: column.headerClassName || '',
      column,
      children: [],
    };
  }

  getHeaderDepth(node: HeaderNodeModel): number {
    if (!node.children || node.children.length === 0) return 1;
    return 1 + Math.max(...node.children.map((child) => this.getHeaderDepth(child)));
  }

  collectHeaderLeafColumns(node: HeaderNodeModel): RenderColumn[] {
    if (node.column) return [node.column];
    if (!node.children || node.children.length === 0) return [];
    return node.children.flatMap((child) => this.collectHeaderLeafColumns(child));
  }

  appendHeaderCells(
    node: HeaderNodeModel,
    depth: number,
    maxDepth: number,
    rows: HeaderCellModel[][],
    frozenByKey: Map<string, boolean>,
    path: string,
  ): void {
    const leaves = this.collectHeaderLeafColumns(node);
    if (leaves.length === 0) return;

    const isLeaf = Boolean(node.column) || node.children.length === 0;
    const frozen = leaves.every((column) => frozenByKey.get(column.key) === true);
    const widthPx = leaves.reduce((sum, column) => sum + column.widthPx, 0);
    const minWidthPx = leaves.reduce((sum, column) => sum + this.toPixels(column.minWidth, column.widthPx), 0);
    const needsSerialSpacer = isLeaf && node.column?.type === 'serial' && maxDepth - depth > 1;
    const rowspan = isLeaf && !needsSerialSpacer ? maxDepth - depth : 1;

    rows[depth].push({
      id: `${path}-${node.id}-${depth}`,
      key: node.key || node.column?.key,
      label: node.label,
      className: node.className || '',
      colspan: leaves.length,
      rowspan,
      depth,
      widthPx,
      minWidthPx,
      leftOffset: frozen ? leaves[0]?.leftOffset || 0 : 0,
      frozen,
      sortable: isLeaf && !!node.column && this.isColumnSortable(node.column),
      column: isLeaf ? node.column : undefined,
    });

    if (needsSerialSpacer && node.column) {
      rows[depth + 1].push({
        id: `${path}-${node.id}-${depth}-serial-spacer`,
        key: node.column.key,
        label: '',
        className: [node.className || '', 'serial-header-spacer'].filter(Boolean).join(' '),
        colspan: 1,
        rowspan: maxDepth - depth - 1,
        depth: depth + 1,
        widthPx,
        minWidthPx,
        leftOffset: frozen ? leaves[0]?.leftOffset || 0 : 0,
        frozen,
        sortable: false,
        column: node.column,
        placeholder: true,
      });
    }

    if (!isLeaf) {
      node.children.forEach((child, index) => {
        this.appendHeaderCells(child, depth + 1, maxDepth, rows, frozenByKey, `${path}-${index}`);
      });
    }
  }

  getHeaderStackHeight(): number {
    const rowCount = this.headerRows.length > 0 ? this.headerRows.length : 1;
    return this.sumMeasuredHeights(this.measuredHeaderRowHeights, rowCount, this.headerRowHeight);
  }

  protected getHeaderRowTopOffset(rowIndex: number): number {
    return this.sumMeasuredHeights(this.measuredHeaderRowHeights, rowIndex, this.headerRowHeight);
  }

  getHeaderCellSortIndicator(cell: HeaderCellModel): string {
    if (!cell.column) return '';
    return this.getSortIndicator(cell.column);
  }

  onHeaderCellClick(cell: HeaderCellModel, event?: MouseEvent): void {
    event?.preventDefault();
    event?.stopPropagation();
    if (!cell.column || !cell.sortable) return;
    this.onHeaderClick(cell.column);
  }

  isSortEnabled(): boolean {
    return this.data?.sort?.enabled !== false;
  }

  isColumnSortable(column: RenderColumn): boolean {
    return this.isSortEnabled() && column.sortable !== false;
  }

  onHeaderClick(column: RenderColumn): void {
    if (!this.isColumnSortable(column)) return;

    if (this.sortKey !== column.key) {
      this.sortKey = column.key;
      this.sortDirection = 'asc';
    } else if (this.sortDirection === 'asc') {
      this.sortDirection = 'desc';
    } else if (this.sortDirection === 'desc') {
      this.sortDirection = 'none';
    } else {
      this.sortDirection = 'asc';
    }

    this.usingDefaultSort = false;
    this.activePopup = null;
    this.rebuildTableModel();
  }

  getSortIndicator(column: RenderColumn): string {
    if (this.sortKey !== column.key || this.sortDirection === 'none') return '';
    return this.sortDirection === 'asc' ? '^' : 'v';
  }

  sortRows(rows: any[]): any[] {
    if (rows.length === 0) return rows;
    if (this.usingDefaultSort && this.data?.defaultSort?.key) {
      this.sortKey = this.data.defaultSort.key;
      this.sortDirection = this.data.defaultSort.direction || 'asc';
    }
    if (!this.sortKey || this.sortDirection === 'none') {
      return rows.map((row) => this.sortChildren(row));
    }

    const col = this.visibleColumns.find((c) => c.key === this.sortKey);
    const sorted = [...rows].sort((a, b) => this.compareByColumn(a, b, col));
    return sorted.map((row) => this.sortChildren(row));
  }

  sortChildren(row: any): any {
    const cloned = this.cloneObject(row);
    const children = cloned?.[this.childKey];
    if (!Array.isArray(children) || children.length === 0) return cloned;
    cloned[this.childKey] = this.sortRows(children);
    return cloned;
  }

  compareByColumn(a: any, b: any, col?: RenderColumn): number {
    const key = this.sortKey;
    const left = a?.[key];
    const right = b?.[key];
    if (left === right) return 0;
    if (left === undefined || left === null || left === '') return 1;
    if (right === undefined || right === null || right === '') return -1;

    const type = col?.type || 'text';
    let result = 0;
    if (type === 'number' || this.isNumeric(left) || this.isNumeric(right)) {
      result = Number(left) - Number(right);
    } else if (this.isDateLike(left) && this.isDateLike(right)) {
      result = new Date(left).getTime() - new Date(right).getTime();
    } else {
      result = String(left).localeCompare(String(right), undefined, { numeric: true, sensitivity: 'base' });
    }

    return this.sortDirection === 'desc' ? -result : result;
  }

  flattenRows(rows: any[]): FlatRow[] {
    const flattened: FlatRow[] = [];
    const walk = (nodes: any[], depth: number, parentId: string | null): void => {
      nodes.forEach((row, index) => {
        const rowId = this.buildRowId(row, depth, index, parentId);
        const children = row?.[this.childKey];
        const hasChildren = Array.isArray(children) && children.length > 0;
        const expanded = hasChildren ? this.isRowExpanded(row, rowId, depth) : false;

        flattened.push({ id: rowId, depth, source: row, hasChildren, expanded });
        if (hasChildren && expanded) walk(children, depth + 1, rowId);
      });
    };
    walk(rows, 0, null);
    return flattened;
  }

  buildRowId(row: any, depth: number, index: number, parentId: string | null): string {
    const rowIdKey = this.data?.drillDown?.rowIdKey || 'id';
    const explicit = row?.[rowIdKey] ?? row?._id;
    const local = explicit !== undefined && explicit !== null ? String(explicit) : `${depth}-${index}`;
    return parentId ? `${parentId}::${local}` : local;
  }

  isRowExpanded(row: any, rowId: string, depth: number): boolean {
    if (this.expandedState[rowId] !== undefined) return this.expandedState[rowId];

    const expandedKey = this.data?.drillDown?.expandedKey;
    if (expandedKey) {
      const value = this.getValueByPath(row, expandedKey);
      if (value !== undefined) return Boolean(value);
    }

    const defaultLevel = Math.max(0, Number(this.data?.drillDown?.defaultExpandedLevel ?? 1));
    return depth < defaultLevel;
  }

  rebuildBodyCellSpans(): void {
    this.bodyCellSpans.clear();
    this.coveredBodyCells.clear();
    if (!this.isBodyCellMergingEnabled()) return;

    this.applyCellConfigSpans();
    this.applyConfiguredMergeRanges();
    this.applyAutoMergeRules();
  }

  protected isBodyCellMergingEnabled(): boolean {
    const configs = [this.data?.cellMerges, this.data?.mergeCells, this.data?.bodyMerges];
    return !configs.some((config) => !!config && !Array.isArray(config) && config.enabled === false);
  }

  protected applyCellConfigSpans(): void {
    this.flatRows.forEach((row, rowIndex) => {
      this.visibleColumns.forEach((column, colIndex) => {
        const cellConfig = this.getCellConfig(row.source, column.key);
        const rowSpan = this.resolveSpanCount(cellConfig?.rowSpan ?? cellConfig?.rowspan ?? cellConfig?.rows, 1);
        const colSpan = this.resolveSpanCount(cellConfig?.colSpan ?? cellConfig?.colspan ?? cellConfig?.columnSpan, 1);
        if (rowSpan > 1 || colSpan > 1) {
          this.addBodyCellSpan(rowIndex, colIndex, rowSpan, colSpan, cellConfig?.className);
        }
      });
    });
  }

  protected applyConfiguredMergeRanges(): void {
    for (const range of this.getConfiguredMergeRanges()) {
      const rowIndex = this.resolveMergeStartRowIndex(range);
      const colIndex = this.resolveMergeStartColumnIndex(range);
      const rowSpan = this.resolveSpanCount(range.rowSpan ?? range.rowspan ?? range.rows, 1);
      const colSpan = this.resolveSpanCount(range.colSpan ?? range.colspan ?? range.columnSpan, 1);
      this.addBodyCellSpan(rowIndex, colIndex, rowSpan, colSpan, range.className);
    }
  }

  protected applyAutoMergeRules(): void {
    for (const rule of this.getConfiguredMergeRules()) {
      const direction = rule.direction || 'vertical';
      if (direction !== 'vertical') continue;
      const columns = this.resolveMergeRuleColumns(rule);
      for (const columnKey of columns) {
        const colIndex = this.visibleColumns.findIndex((column) => column.key === columnKey);
        if (colIndex < 0) continue;
        this.applyVerticalMergeRule(rule, colIndex);
      }
    }
  }

  protected applyVerticalMergeRule(rule: SuperCellMergeRule, colIndex: number): void {
    const minSpan = Math.max(2, this.resolveSpanCount(rule.minSpan, 2));
    let runStart = -1;
    let runSignature = '';

    const flush = (endIndex: number): void => {
      if (runStart < 0) return;
      const length = endIndex - runStart;
      if (length >= minSpan) {
        this.addBodyCellSpan(runStart, colIndex, length, 1, rule.className);
      }
      runStart = -1;
      runSignature = '';
    };

    for (let rowIndex = 0; rowIndex <= this.flatRows.length; rowIndex += 1) {
      const row = this.flatRows[rowIndex];
      const signature = row ? this.getMergeRuleSignature(row, this.visibleColumns[colIndex], rule) : '';
      if (!signature) {
        flush(rowIndex);
        continue;
      }
      if (runStart < 0 || signature !== runSignature) {
        flush(rowIndex);
        runStart = rowIndex;
        runSignature = signature;
      }
    }
  }

  protected getMergeRuleSignature(row: FlatRow, column: RenderColumn, rule: SuperCellMergeRule): string {
    if (!rule.includeGroupRows && this.isGroupRow(row.source)) return '';
    const keys = Array.isArray(rule.matchBy) && rule.matchBy.length > 0 ? rule.matchBy : [column.key];
    const values = keys.map((key) => this.getValueByPath(row.source, key));
    if (!rule.includeEmpty && values.some((value) => value === undefined || value === null || value === '')) return '';
    const depthPart = rule.matchDepth === false ? '' : `depth:${row.depth}|`;
    return `${depthPart}${JSON.stringify(values)}`;
  }

  protected isGroupRow(row: any): boolean {
    const className = String(this.getRowConfig(row)?.className || '');
    return className.split(/\s+/).includes('row-group');
  }

  protected getConfiguredMergeRanges(): SuperCellMergeRange[] {
    const ranges: SuperCellMergeRange[] = [];
    for (const config of this.getMergeConfigSources()) {
      if (Array.isArray(config.ranges)) ranges.push(...config.ranges);
      if (Array.isArray(config.cells)) ranges.push(...config.cells);
      if (Array.isArray(config.merges)) ranges.push(...config.merges);
    }
    if (Array.isArray(this.data?.cellMergeRanges)) ranges.push(...this.data!.cellMergeRanges!);
    return ranges;
  }

  protected getConfiguredMergeRules(): SuperCellMergeRule[] {
    const rules: SuperCellMergeRule[] = [];
    for (const config of this.getMergeConfigSources()) {
      if (Array.isArray(config.rules)) rules.push(...config.rules);
    }
    if (Array.isArray(this.data?.cellMergeRules)) rules.push(...this.data!.cellMergeRules!);

    const autoMerge = this.data?.autoMergeCells;
    if (autoMerge === true) {
      rules.push({ columns: this.visibleColumns.map((column) => column.key), direction: 'vertical' });
    } else if (autoMerge && typeof autoMerge === 'object' && Array.isArray(autoMerge.columns)) {
      rules.push({
        columns: autoMerge.columns,
        direction: 'vertical',
        includeEmpty: autoMerge.includeEmpty,
        includeGroupRows: autoMerge.includeGroupRows,
        matchDepth: autoMerge.matchDepth,
      });
    }

    return rules;
  }

  protected getMergeConfigSources(): SuperCellMergeConfig[] {
    return [this.data?.cellMerges, this.data?.mergeCells, this.data?.bodyMerges]
      .map((config) => this.normalizeMergeConfig(config))
      .filter((config): config is SuperCellMergeConfig => !!config);
  }

  protected normalizeMergeConfig(config: SuperConfig['cellMerges']): SuperCellMergeConfig | null {
    if (!config) return null;
    if (Array.isArray(config)) return { ranges: config };
    if (typeof config === 'object') return config;
    return null;
  }

  protected resolveMergeRuleColumns(rule: SuperCellMergeRule): string[] {
    const columns = Array.isArray(rule.columns) ? rule.columns : [];
    const single = rule.columnKey || rule.key;
    return [...columns, single]
      .map((key) => String(key || '').trim())
      .filter((key, index, all) => !!key && all.indexOf(key) === index);
  }

  protected resolveMergeStartRowIndex(range: SuperCellMergeRange): number {
    const index = Number(range.startRowIndex ?? range.rowIndex);
    if (Number.isInteger(index) && index >= 0) return index;

    const rowId = String(range.startRowId ?? range.rowId ?? '').trim();
    if (rowId) return this.flatRows.findIndex((row) => row.id === rowId);

    const rowKey = String(range.rowKey || '').trim();
    if (rowKey) {
      return this.flatRows.findIndex((row) => this.areValuesEqual(this.getValueByPath(row.source, rowKey), range.rowValue));
    }

    return -1;
  }

  protected resolveMergeStartColumnIndex(range: SuperCellMergeRange): number {
    const index = Number(range.startColumnIndex ?? range.columnIndex);
    if (Number.isInteger(index) && index >= 0) return index;

    const columnKey = String(range.startColumnKey ?? range.columnKey ?? '').trim();
    if (columnKey) return this.visibleColumns.findIndex((column) => column.key === columnKey);

    return -1;
  }

  protected resolveSpanCount(value: any, fallback: number): number {
    const parsed = Number(value);
    if (!Number.isFinite(parsed)) return fallback;
    return Math.max(1, Math.floor(parsed));
  }

  protected addBodyCellSpan(
    rowIndex: number,
    colIndex: number,
    rowSpan: number,
    colSpan: number,
    className?: string,
  ): boolean {
    if (rowIndex < 0 || colIndex < 0 || rowIndex >= this.flatRows.length || colIndex >= this.visibleColumns.length) {
      return false;
    }
    const effectiveRowSpan = Math.max(1, Math.min(rowSpan, this.flatRows.length - rowIndex));
    const effectiveColSpan = Math.max(1, Math.min(colSpan, this.visibleColumns.length - colIndex));
    if (effectiveRowSpan === 1 && effectiveColSpan === 1) return false;

    for (let r = rowIndex; r < rowIndex + effectiveRowSpan; r += 1) {
      for (let c = colIndex; c < colIndex + effectiveColSpan; c += 1) {
        const key = this.getBodyCellKey(r, c);
        if (this.bodyCellSpans.has(key) || this.coveredBodyCells.has(key)) {
          return false;
        }
      }
    }

    this.bodyCellSpans.set(this.getBodyCellKey(rowIndex, colIndex), {
      rowSpan: effectiveRowSpan,
      colSpan: effectiveColSpan,
      className,
    });

    for (let r = rowIndex; r < rowIndex + effectiveRowSpan; r += 1) {
      for (let c = colIndex; c < colIndex + effectiveColSpan; c += 1) {
        if (r === rowIndex && c === colIndex) continue;
        this.coveredBodyCells.add(this.getBodyCellKey(r, c));
      }
    }

    return true;
  }

  isDrillDownEnabled(): boolean {
    if (this.data?.drillDown?.enabled === true) return true;
    return this.rawRows.some((row) => Array.isArray(row?.[this.childKey]) && row[this.childKey].length > 0);
  }

  showToggleInColumn(column: RenderColumn, columnIndex: number): boolean {
    if (!this.isDrillDownEnabled()) return false;
    if (column.drillDownToggle) return true;
    if (this.toggleColumnKey) return column.key === this.toggleColumnKey;
    return columnIndex === 0;
  }

  toggleRow(row: FlatRow, event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    if (!row.hasChildren) return;
    this.expandedState[row.id] = !row.expanded;
    this.rebuildTableModel();
  }

  getRowSerial(rowIndex: number): number {
    return rowIndex + 1;
  }

  getSelectionKeyForSerialColumn(column: RenderColumn): string {
    const key = String(column.selectionKey || '').trim();
    return key || DEFAULT_SERIAL_SELECTION_KEY;
  }

  isRowSelected(row: FlatRow, column: RenderColumn): boolean {
    const selectionKey = this.getSelectionKeyForSerialColumn(column);
    const fromState = this.selectedState[row.id];
    if (fromState !== undefined) return Boolean(fromState);

    const fromRow = this.getValueByPath(row.source, selectionKey);
    if (fromRow !== undefined) return Boolean(fromRow);
    return false;
  }

  onSerialSelectionChange(row: FlatRow, column: RenderColumn, event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    const checked = Boolean((event.target as HTMLInputElement | null)?.checked);
    this.setRowSelected(row, column, checked);
  }

  isAllRowsSelected(column: RenderColumn): boolean {
    const nodes = this.getSelectionNodes();
    if (nodes.length === 0) return false;
    return nodes.every((node) => this.isSelectionNodeSelected(node, column));
  }

  isSomeRowsSelected(column: RenderColumn): boolean {
    const nodes = this.getSelectionNodes();
    if (nodes.length === 0) return false;

    let anySelected = false;
    let allSelected = true;

    for (const node of nodes) {
      const selected = this.isSelectionNodeSelected(node, column);
      anySelected = anySelected || selected;
      allSelected = allSelected && selected;
      if (anySelected && !allSelected) return true;
    }

    return anySelected && !allSelected;
  }

  onSelectAllRowsChange(column: RenderColumn, event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    const checked = Boolean((event.target as HTMLInputElement | null)?.checked);
    this.setAllRowsSelected(column, checked);
  }

  protected setAllRowsSelected(column: RenderColumn, selected: boolean): void {
    const nodes = this.getSelectionNodes();
    for (const node of nodes) {
      this.selectedState[node.id] = selected;
    }

    this.cdr.markForCheck();
    this.emitCustomEvent(this.data?.selectionEventName || this.data?.actionEventName, {
      widgetId: this.data?.widgetId,
      action: 'rowSelectionAll',
      selected,
      selectedRowIds: this.getSelectedRowIds(),
    });
  }

  protected isSelectionNodeSelected(node: { id: string; source: any }, column: RenderColumn): boolean {
    const fromState = this.selectedState[node.id];
    if (fromState !== undefined) return Boolean(fromState);
    const selectionKey = this.getSelectionKeyForSerialColumn(column);
    const fromRow = this.getValueByPath(node.source, selectionKey);
    if (fromRow !== undefined) return Boolean(fromRow);
    return false;
  }

  protected getSelectionNodes(): Array<{ id: string; source: any }> {
    const nodes: Array<{ id: string; source: any }> = [];
    const walk = (rows: any[], depth: number, parentId: string | null): void => {
      rows.forEach((row, index) => {
        const id = this.buildRowId(row, depth, index, parentId);
        nodes.push({ id, source: row });
        const children = row?.[this.childKey];
        if (Array.isArray(children) && children.length > 0) {
          walk(children, depth + 1, id);
        }
      });
    };

    if (Array.isArray(this.rawRows) && this.rawRows.length > 0) {
      walk(this.rawRows, 0, null);
    }

    return nodes;
  }

  setRowSelected(row: FlatRow, column: RenderColumn, selected: boolean): void {
    this.selectedState[row.id] = selected;

    const cascade = column.cascadeSelection === true;
    if (cascade && row.hasChildren) {
      const children = row.source?.[this.childKey];
      if (Array.isArray(children)) {
        this.applySelectionToDescendants(children, row.depth + 1, row.id, selected);
      }
    }

    this.cdr.markForCheck();
    this.emitCustomEvent(this.data?.selectionEventName || this.data?.actionEventName, {
      widgetId: this.data?.widgetId,
      action: 'rowSelection',
      rowId: row.id,
      selected,
      selectedRowIds: this.getSelectedRowIds(),
    });
  }

  applySelectionToDescendants(nodes: any[], depth: number, parentId: string, selected: boolean): void {
    nodes.forEach((node, index) => {
      const rowId = this.buildRowId(node, depth, index, parentId);
      this.selectedState[rowId] = selected;
      const children = node?.[this.childKey];
      if (Array.isArray(children) && children.length > 0) {
        this.applySelectionToDescendants(children, depth + 1, rowId, selected);
      }
    });
  }

  getSelectedRowIds(): string[] {
    return Object.keys(this.selectedState).filter((id) => this.selectedState[id] === true);
  }

  isFrozenColumn(index: number, column: SuperColumn): boolean {
    return column.sticky === 'left' || index < this.freezeLeftColumns;
  }

  isLastFrozenColumn(index: number, column: SuperColumn): boolean {
    if (!this.isFrozenColumn(index, column)) return false;
    const nextColumn = this.visibleColumns[index + 1];
    return !nextColumn || !this.isFrozenColumn(index + 1, nextColumn);
  }

  isLastFrozenRenderColumn(column?: RenderColumn): boolean {
    if (!column) return false;
    const index = this.visibleColumns.findIndex((item) => item.key === column.key);
    return index >= 0 && this.isLastFrozenColumn(index, column);
  }

  isFrozenRow(rowIndex: number): boolean {
    return rowIndex < this.freezeTopRows;
  }

  getHeaderCellStyle(cell: HeaderCellModel, rowIndex: number): Record<string, string | number> {
    const style: Record<string, string | number> = {
      width: `${cell.widthPx}px`,
      'min-width': `${cell.minWidthPx || cell.widthPx}px`,
      'text-align': cell.column?.align || 'left',
      height: `${Math.max(this.headerRowHeight, this.headerRowHeight * Math.max(1, cell.rowspan))}px`,
    };

    if (this.freezeHeader || cell.frozen) {
      style['position'] = 'sticky';
      style['z-index'] = this.freezeHeader
        ? cell.frozen ? 620 - rowIndex : 560 - rowIndex
        : cell.frozen ? 430 : 40;
    }

    if (this.freezeHeader) {
      style['top'] = `${this.getHeaderRowTopOffset(rowIndex)}px`;
    }

    if (cell.frozen) {
      style['left'] = `${cell.leftOffset}px`;
    }
    return style;
  }

  getBodyCellStyle(row: FlatRow, rowIndex: number, column: RenderColumn, colIndex: number): Record<string, string | number> {
    const frozenRow = this.isFrozenRow(rowIndex);
    const frozenCol = this.isFrozenColumn(colIndex, column);
    const rowCfg = this.getRowConfig(row.source);
    const span = this.getBodyCellSpan(rowIndex, colIndex);
    const rowHeight = this.getBodyCellHeight(rowIndex, span.rowSpan);
    const widthPx = this.getBodyCellWidth(colIndex, span.colSpan);
    const minWidthPx = this.getBodyCellMinWidth(colIndex, span.colSpan);

    const style: Record<string, string | number> = {
      width: `${widthPx}px`,
      'min-width': `${minWidthPx}px`,
      'text-align': column.align || 'left',
      height: `${rowHeight}px`,
    };

    if (frozenRow || frozenCol) {
      style['position'] = 'sticky';
      style['z-index'] = frozenRow && frozenCol ? 460 : frozenRow ? 450 : 430;
      style['background'] = rowCfg?.backgroundColor || this.getStickyCellBackground(rowIndex);
      style['color'] = `var(--dvm-content-text, ${this.resolvedTheme?.layout?.content?.text || '#0f172a'})`;
    }
    if (frozenRow) style['top'] = `${this.getHeaderStackHeight() + this.getFrozenBodyRowsHeight(rowIndex)}px`;
    if (frozenCol) style['left'] = `${column.leftOffset}px`;
    return style;
  }

  protected getStickyCellBackground(rowIndex: number): string {
    const tableTheme = this.resolvedTheme?.table;
    return rowIndex % 2 === 0
      ? `var(--dvm-table-row-bg, ${tableTheme?.rowBg || '#ffffff'})`
      : `var(--dvm-table-alt-row-bg, ${tableTheme?.altRowBg || '#f8fafc'})`;
  }

  getRowHeightForIndex(rowIndex: number): number {
    const measured = this.measuredFrozenRowHeights[rowIndex];
    if (rowIndex < this.freezeTopRows && Number.isFinite(measured) && measured > 0) {
      return measured;
    }
    if (rowIndex < this.freezeTopRows && this.frozenTopRowHeight > 0) {
      return this.frozenTopRowHeight;
    }
    return this.rowHeight;
  }

  getFrozenBodyRowsHeight(rowIndex: number): number {
    const frozenCount = Math.max(0, Math.min(rowIndex, this.freezeTopRows));
    if (frozenCount === 0) return 0;
    if (this.measuredFrozenRowHeights.length > 0) {
      return this.sumMeasuredHeights(this.measuredFrozenRowHeights, frozenCount, this.frozenTopRowHeight || this.rowHeight);
    }
    if (this.frozenTopRowHeight === this.rowHeight) return frozenCount * this.rowHeight;
    let total = 0;
    for (let i = 0; i < frozenCount; i += 1) {
      total += this.getRowHeightForIndex(i);
    }
    return total;
  }
  getRowClass(row: FlatRow, rowIndex: number): string {
    const rowCfg = this.getRowConfig(row.source);
    return [
      rowIndex % 2 === 0 ? 'even-row' : 'odd-row',
      rowCfg?.className || '',
      this.isRowEditable(row.source) ? 'row-editable' : 'row-readonly',
      row.depth > 0 ? 'row-child' : 'row-root',
    ]
      .filter(Boolean)
      .join(' ');
  }

  getCellClass(row: FlatRow, rowIndex: number, column: RenderColumn, colIndex: number): string {
    const resolved = this.getResolvedCellConfig(row.source, column);
    const span = this.getBodyCellSpan(rowIndex, colIndex);
    return [
      column.className || '',
      resolved.className || '',
      span.className || '',
      span.rowSpan > 1 || span.colSpan > 1 ? 'merged-cell' : '',
      this.isFrozenColumn(colIndex, column) ? 'frozen-column' : '',
      this.isLastFrozenColumn(colIndex, column) || this.isMergedCellFrozenEdge(colIndex, span) ? 'frozen-column-edge' : '',
      this.isFrozenRow(rowIndex) ? 'frozen-row-cell' : '',
      this.isCellEditable(row.source, column) ? 'editable-cell' : 'read-only-cell',
      `depth-${Math.min(row.depth, 6)}`,
    ]
      .filter(Boolean)
      .join(' ');
  }

  shouldRenderBodyCell(rowIndex: number, colIndex: number): boolean {
    return !this.coveredBodyCells.has(this.getBodyCellKey(rowIndex, colIndex));
  }

  getBodyCellRowSpan(rowIndex: number, colIndex: number): number | null {
    const span = this.getBodyCellSpan(rowIndex, colIndex);
    return span.rowSpan > 1 ? span.rowSpan : null;
  }

  getBodyCellColSpan(rowIndex: number, colIndex: number): number | null {
    const span = this.getBodyCellSpan(rowIndex, colIndex);
    return span.colSpan > 1 ? span.colSpan : null;
  }

  getBodyCellSpan(rowIndex: number, colIndex: number): BodyCellSpan {
    return this.bodyCellSpans.get(this.getBodyCellKey(rowIndex, colIndex)) || { rowSpan: 1, colSpan: 1 };
  }

  protected getBodyCellKey(rowIndex: number, colIndex: number): string {
    return `${rowIndex}:${colIndex}`;
  }

  protected getBodyCellWidth(colIndex: number, colSpan: number): number {
    const count = Math.max(1, colSpan);
    const columns = this.visibleColumns.slice(colIndex, colIndex + count);
    const width = columns.reduce((sum, item) => sum + item.widthPx, 0);
    return width || this.visibleColumns[colIndex]?.widthPx || this.defaultColumnWidth;
  }

  protected getBodyCellMinWidth(colIndex: number, colSpan: number): number {
    const count = Math.max(1, colSpan);
    const columns = this.visibleColumns.slice(colIndex, colIndex + count);
    const width = columns.reduce((sum, item) => sum + item.minWidthPx, 0);
    return width || this.visibleColumns[colIndex]?.minWidthPx || this.defaultColumnWidth;
  }

  protected getBodyCellHeight(rowIndex: number, rowSpan: number): number {
    const count = Math.max(1, rowSpan);
    let total = 0;
    for (let i = rowIndex; i < Math.min(this.flatRows.length, rowIndex + count); i += 1) {
      total += this.getRowHeightForIndex(i);
    }
    return total || this.getRowHeightForIndex(rowIndex);
  }

  protected isMergedCellFrozenEdge(colIndex: number, span: BodyCellSpan): boolean {
    if (span.colSpan <= 1) return false;
    const endIndex = Math.min(this.visibleColumns.length - 1, colIndex + span.colSpan - 1);
    const endColumn = this.visibleColumns[endIndex];
    return !!endColumn && this.isFrozenColumn(endIndex, endColumn);
  }

  getIndentPadding(depth: number): string {
    return `${Math.max(0, depth) * 16}px`;
  }

  resolveCellType(row: any, column: RenderColumn): CellType {
    const resolved = this.getResolvedCellConfig(row, column);
    if (resolved.type === 'serial') return 'serial';
    const value = row?.[column.key];
    if (resolved.type === 'text' && this.hasChartPayload(value)) return 'chart';
    return (resolved.type || 'text') as CellType;
  }

  isCellEditable(row: any, column: RenderColumn): boolean {
    const type = this.resolveCellType(row, column);
    if (['action', 'chart', 'link', 'image', 'serial'].includes(type)) return false;
    if (!this.isRowEditable(row)) return false;
    const resolved = this.getResolvedCellConfig(row, column);
    if (resolved.disabled) return false;
    return resolved.editable ?? this.data?.editable ?? true;
  }

  isRowEditable(row: any): boolean {
    if (this.data?.editable === false) return false;
    const rowCfg = this.getRowConfig(row);
    if (rowCfg?.disabled || rowCfg?.editable === false) return false;

    const rowEditableKey = this.data?.rowEditableKey;
    if (!rowEditableKey) return true;

    const value = this.getValueByPath(row, rowEditableKey);
    if (Array.isArray(this.data?.rowEditableWhen) && this.data!.rowEditableWhen!.length > 0) {
      return this.data!.rowEditableWhen!.includes(value);
    }

    return Boolean(value);
  }

  getResolvedCellConfig(row: any, column: RenderColumn): RenderColumn {
    const cellCfg = this.getCellConfig(row, column.key);
    const rowCfg = this.getRowConfig(row);
    return {
      ...column,
      ...cellCfg,
      options: cellCfg.options ?? column.options,
      editable: cellCfg.editable ?? rowCfg?.editable ?? column.editable,
      disabled: cellCfg.disabled ?? rowCfg?.disabled ?? column.disabled,
      type: cellCfg.type || column.type || 'text',
    };
  }

  getCellValue(row: any, column: RenderColumn): any {
    return row?.[column.key];
  }

  getDisplayValue(row: any, column: RenderColumn): string {
    const value = this.getCellValue(row, column);
    const resolved = this.getResolvedCellConfig(row, column);
    if (value === undefined || value === null || value === '') return '—';

    if (resolved.type === 'checkbox') return value ? resolved.trueLabel || 'True' : resolved.falseLabel || 'False';

    if (resolved.type === 'select') {
      const options = this.resolveOptions(row, resolved);
      const selected = options.find((opt) => this.areValuesEqual(opt.value, value));
      return selected?.label || String(value);
    }

    if (resolved.type === 'image') return '';
    if (resolved.type === 'chart' && this.resolveChartConfig(row, resolved)) return '';
    return String(value);
  }

  resolveImageSource(value: any): string {
    const raw = this.resolveImageValue(value);
    if (!raw) return '';
    const normalized = String(raw).trim();
    if (!normalized) return '';

    if (/^(https?:\/\/|data:|blob:|\/|assets\/|\.\/|\.\.\/)/i.test(normalized)) {
      return normalized;
    }

    if (/^[a-zA-Z]:\\/.test(normalized)) {
      return `file:///${normalized.replace(/\\/g, '/')}`;
    }

    return normalized;
  }

  resolveImageValue(value: any): any {
    if (value === undefined || value === null) return '';
    if (typeof value === 'string') return value;
    if (Array.isArray(value)) return this.resolveImageValue(value[0]);
    if (typeof value === 'object') {
      return value.url || value.src || value.path || value.filePath || value.file || '';
    }
    return '';
  }

  getInputType(row: any, column: RenderColumn): string {
    const resolved = this.getResolvedCellConfig(row, column);
    if (resolved.inputType) return resolved.inputType;
    return resolved.type === 'number' ? 'number' : 'text';
  }

  getPlaceholder(row: any, column: RenderColumn): string {
    return this.getResolvedCellConfig(row, column).placeholder || '';
  }

  resolveOptions(row: any, column: SuperColumn): SuperOption[] {
    const resolved = this.getResolvedCellConfig(row, column as RenderColumn);
    const rowOptions = resolved.optionsPath ? this.getValueByPath(row, resolved.optionsPath) : undefined;
    if (Array.isArray(rowOptions)) return this.normalizeOptions(rowOptions);
    if (Array.isArray(resolved.options)) return this.normalizeOptions(resolved.options);
    const value = row?.[resolved.key];
    if (Array.isArray(value?.options)) return this.normalizeOptions(value.options);
    return [];
  }

  normalizeOptions(options: any[] | undefined): SuperOption[] {
    if (!Array.isArray(options)) return [];
    return options.map((item: any) => {
      if (item && typeof item === 'object' && !Array.isArray(item)) {
        return {
          label: item.label ?? item.name ?? String(item.value ?? ''),
          value: item.value ?? item.id ?? item.key ?? item.label ?? item.name,
        };
      }
      return { label: String(item), value: item };
    });
  }

  onTextInput(row: FlatRow, column: RenderColumn, event: Event): void {
    this.applyCellValue(row, column, (event.target as HTMLInputElement | HTMLTextAreaElement).value);
  }

  onSelectChange(row: FlatRow, column: RenderColumn, event: Event): void {
    this.applyCellValue(row, column, (event.target as HTMLSelectElement).value);
  }

  onCheckboxChange(row: FlatRow, column: RenderColumn, event: Event): void {
    this.applyCellValue(row, column, (event.target as HTMLInputElement).checked);
  }

  applyCellValue(row: FlatRow, column: RenderColumn, value: any): void {
    row.source[column.key] = value;
    this.cdr.markForCheck();

    this.emitCustomEvent(this.data?.editEventName, {
      widgetId: this.data?.widgetId,
      rowId: row.id,
      row: this.cloneObject(row.source),
      columnKey: column.key,
      value,
      rows: this.cloneRows(this.rawRows),
    });
  }

  getTooltipText(row: any, column: RenderColumn): string {
    const cellCfg = this.getCellConfig(row, column.key);
    const cfg = cellCfg?.tooltip ?? column.tooltip ?? row?.[column.key]?.tooltip;
    if (!cfg) return '';
    if (typeof cfg === 'string') return cfg;
    if (cfg.text) return String(cfg.text);
    if (cfg.path) {
      const value = this.getValueByPath(row, cfg.path);
      return value === undefined || value === null ? '' : String(value);
    }
    if (cfg.key) {
      const value = row?.[cfg.key];
      return value === undefined || value === null ? '' : String(value);
    }
    return '';
  }

  hasPopup(row: any, column: RenderColumn): boolean {
    return Boolean(this.resolvePopupConfig(row, column));
  }

  resolvePopupConfig(row: any, column: RenderColumn): SuperPopup | string | null {
    const cellCfg = this.getCellConfig(row, column.key);
    if (cellCfg?.popup) return cellCfg.popup;
    if (column.popup) return column.popup;
    const value = row?.[column.key];
    if (value && typeof value === 'object' && value.popup) return value.popup;
    return null;
  }

  togglePopup(row: FlatRow, column: RenderColumn, event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();

    if (this.activePopup?.rowId === row.id && this.activePopup?.columnKey === column.key) {
      this.activePopup = null;
      this.cdr.markForCheck();
      return;
    }

    const trigger = event.currentTarget as HTMLElement;
    const rect = trigger.getBoundingClientRect();
    const popupCfg = this.resolvePopupConfig(row.source, column);
    if (!popupCfg) return;
    const normalized: SuperPopup = typeof popupCfg === 'string' ? { type: 'text', text: popupCfg } : popupCfg;
    const popupApiUrl = this.resolvePopupApiUrl(normalized, row.source);

    const requestId = ++this.popupRequestSequence;
    if (popupApiUrl) {
      this.activePopup = this.buildPopupState(row, column, rect, normalized, undefined, true, '', requestId);
      this.cdr.markForCheck();
      this.loadPopupFromApi(row, column, rect, normalized, popupApiUrl, requestId);
      return;
    }

    this.activePopup = this.buildPopupState(row, column, rect, normalized, undefined, false, '', requestId);
    this.cdr.markForCheck();
  }

  loadPopupFromApi(
    row: FlatRow,
    column: RenderColumn,
    rect: DOMRect,
    popup: SuperPopup,
    apiUrl: string,
    requestId: number,
  ): void {
    if (!apiUrl) return;
    const url = this.resolveRowTemplate(String(apiUrl), row.source);
    const requestConfig = this.buildPopupRequestConfig(row, column, popup);
    const popupFilters = this.buildPopupFilters(row.source, popup);
    const method = popup.method || this.resolveMethod(url);

    this.configService
      .getConfig(url, requestConfig, popupFilters, method, true)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          if (!this.isPopupRequestActive(row.id, column.key, requestId)) return;
          this.activePopup = this.buildPopupState(row, column, rect, popup, response, false, '', requestId);
          this.cdr.markForCheck();
        },
        error: () => {
          if (!this.isPopupRequestActive(row.id, column.key, requestId)) return;
          this.activePopup = this.buildPopupState(
            row,
            column,
            rect,
            popup,
            undefined,
            false,
            'Unable to load popup data.',
            requestId,
          );
          this.cdr.markForCheck();
        },
      });
  }

  isPopupRequestActive(rowId: string, columnKey: string, requestId: number): boolean {
    return Boolean(
      this.activePopup
      && this.activePopup.rowId === rowId
      && this.activePopup.columnKey === columnKey
      && this.activePopup.requestId === requestId,
    );
  }

  buildPopupRequestConfig(row: FlatRow, column: RenderColumn, popup: SuperPopup): any {
    const merged: any = {
      ...this.cloneObject(popup || {}),
      ...(popup.chart && typeof popup.chart === 'object' ? this.cloneObject(popup.chart) : {}),
    };

    delete merged.type;
    delete merged.title;
    delete merged.text;
    delete merged.sourcePath;
    delete merged.responsePath;
    delete merged.dataPath;
    delete merged.table;
    delete merged.chart;
    delete merged.queryParams;
    delete merged.requestParams;

    merged.widgetId =
      merged.widgetId || `${this.data?.widgetId || 'config-driven-super-table'}-popup-${row.id}-${column.key}`;

    return merged;
  }

  buildPopupFilters(rowData: any, popup: SuperPopup): any {
    const base = this.cloneObject(this.currentFilters || {});
    const queryBase = base.queryParams && typeof base.queryParams === 'object' ? base.queryParams : {};
    const requestBase = base.requestParams && typeof base.requestParams === 'object' ? base.requestParams : {};

    return {
      ...base,
      queryParams: {
        ...queryBase,
        ...this.resolvePopupParamMap(popup.queryParams, rowData),
      },
      requestParams: {
        ...requestBase,
        ...this.resolvePopupParamMap(popup.requestParams, rowData),
      },
      useQueryParams: popup.useQueryParams === true || popup.useQueryParam === true,
      useQueryParam: popup.useQueryParam === true || popup.useQueryParams === true,
    };
  }

  resolvePopupParamMap(map: Record<string, any> | undefined, rowData: any): Record<string, any> {
    if (!map || typeof map !== 'object' || Array.isArray(map)) return {};
    return Object.keys(map).reduce((acc: Record<string, any>, key: string) => {
      const value = this.resolvePopupBindingValue(map[key], rowData);
      if (value !== undefined) acc[key] = value;
      return acc;
    }, {});
  }

  resolvePopupBindingValue(binding: any, rowData: any): any {
    if (binding && typeof binding === 'object' && !Array.isArray(binding)) {
      if (binding.path) return this.getValueByPath(rowData, String(binding.path));
      if (Object.prototype.hasOwnProperty.call(binding, 'value')) return binding.value;
      return this.cloneObject(binding);
    }
    if (typeof binding === 'string' && binding.startsWith('$row.')) {
      return this.getValueByPath(rowData, binding.slice(5));
    }
    return binding;
  }

  buildExternalContext(action?: SuperAction): any {
    const selectedRowIds = this.getSelectedRowIds();
    return {
      controls: this.controlValues,
      filters: this.currentFilters || { queryParams: {}, requestParams: {} },
      widget: this.data || {},
      action: action || {},
      rows: this.rawRows,
      selection: { selectedRowIds, count: selectedRowIds.length },
    };
  }

  resolveFilterTemplate(value: string, context: any): string {
    if (!value || value.indexOf('{{') === -1) return value;
    return value.replace(/\{\{\s*([^}]+)\s*\}\}/g, (_: string, path: string) => {
      const resolved = this.resolveContextPath(String(path).trim(), context);
      return encodeURIComponent(String(resolved ?? ''));
    });
  }

  buildExternalFilters(
    queryParamsMap: Record<string, any> | undefined,
    requestParamsMap: Record<string, any> | undefined,
    baseFilters: any,
    useQueryParams: boolean,
    action?: SuperAction,
  ): any {
    const base = this.cloneObject(baseFilters || { queryParams: {}, requestParams: {} });
    const queryBase = base.queryParams && typeof base.queryParams === 'object' ? base.queryParams : {};
    const requestBase = base.requestParams && typeof base.requestParams === 'object' ? base.requestParams : {};
    const context = this.buildExternalContext(action);

    return {
      ...base,
      queryParams: {
        ...queryBase,
        ...this.resolveExternalParamMap(queryParamsMap, context),
      },
      requestParams: {
        ...requestBase,
        ...this.resolveExternalParamMap(requestParamsMap, context),
      },
      useQueryParams,
      useQueryParam: useQueryParams,
    };
  }

  resolveExternalParamMap(map: Record<string, any> | undefined, context: any): Record<string, any> {
    if (!map || typeof map !== 'object' || Array.isArray(map)) return {};
    return Object.keys(map).reduce((acc: Record<string, any>, key: string) => {
      const value = this.resolveExternalBindingValue(map[key], context);
      if (value !== undefined) acc[key] = value;
      return acc;
    }, {});
  }

  resolveExternalBindingValue(binding: any, context: any): any {
    if (binding && typeof binding === 'object' && !Array.isArray(binding)) {
      if (binding.path) {
        return this.resolveContextPath(String(binding.path), context);
      }
      if (Object.prototype.hasOwnProperty.call(binding, 'value')) {
        return binding.value;
      }
      return this.cloneObject(binding);
    }

    if (typeof binding === 'string') {
      if (binding.startsWith('$controls.')) return this.resolveContextPath(`controls.${binding.slice(10)}`, context);
      if (binding.startsWith('$filters.')) return this.resolveContextPath(`filters.${binding.slice(9)}`, context);
      if (binding.startsWith('$widget.')) return this.resolveContextPath(`widget.${binding.slice(8)}`, context);
      if (binding.startsWith('$action.')) return this.resolveContextPath(`action.${binding.slice(8)}`, context);
      if (binding.startsWith('$selection.')) return this.resolveContextPath(`selection.${binding.slice(11)}`, context);
      if (binding === '$rows') return this.resolveContextPath('rows', context);
    }
    return binding;
  }

  resolveContextPath(path: string, context: any): any {
    if (!path) return undefined;
    return String(path)
      .split('.')
      .reduce((acc: any, part: string) => (acc == null ? undefined : acc[part]), context);
  }

  resolveRowTemplate(value: string, rowData: any): string {
    if (!value || value.indexOf('{{') === -1) return value;
    return value.replace(/\{\{\s*([^}]+)\s*\}\}/g, (_: string, path: string) => {
      const resolved = this.getValueByPath(rowData, String(path).trim());
      return encodeURIComponent(String(resolved ?? ''));
    });
  }

  resolvePopupSource(popup: SuperPopup, rowSource: any, apiResponse?: any): any {
    const source = apiResponse !== undefined ? apiResponse : rowSource;
    const paths = [popup.responsePath, popup.dataPath, popup.sourcePath].filter(Boolean);
    for (const path of paths) {
      const value = this.getValueByPath(source, String(path));
      if (value !== undefined) return value;
    }
    return apiResponse !== undefined ? apiResponse : undefined;
  }

  resolvePopupApiUrl(popup: SuperPopup, rowSource: any): string {
    const direct = popup.apiUrl || popup.chart?.['apiUrl'];
    if (!direct) return '';
    return this.resolveRowTemplate(String(direct), rowSource);
  }

  resolvePopupType(popup: SuperPopup, source: any): 'text' | 'table' | 'chart' {
    if (popup.type) return popup.type;
    if (popup.table || Array.isArray(source)) return 'table';
    if (popup.chart || this.hasChartPayload(source)) return 'chart';
    return 'text';
  }

  buildPopupState(
    row: FlatRow,
    column: RenderColumn,
    rect: DOMRect,
    popupOverride?: SuperPopup,
    apiResponse?: any,
    loading = false,
    error = '',
    requestId = 0,
  ): PopupState | null {
    const popupCfg = popupOverride || this.resolvePopupConfig(row.source, column);
    if (!popupCfg) return null;

    const normalized: SuperPopup = typeof popupCfg === 'string' ? { type: 'text', text: popupCfg } : popupCfg;
    const source = this.resolvePopupSource(normalized, row.source, apiResponse);
    const type = this.resolvePopupType(normalized, source);

    const title = normalized.title || column.label || column.key;
    const left = Math.max(12, Math.min(rect.left, (window.innerWidth || 1200) - 460));
    const top = rect.bottom + 8;

    if (loading || error) {
      return {
        rowId: row.id,
        columnKey: column.key,
        title,
        type,
        text: normalized.text || '',
        tableRows: [],
        tableColumns: [],
        chart: null,
        top,
        left,
        loading,
        error,
        requestId,
      };
    }

    if (type === 'table') {
      const tableRows = normalized.table?.rows || (Array.isArray(source) ? source : []);
      const tableColumns = (normalized.table?.columns || this.buildTableColumnsFromRows(tableRows)).map((item) => ({
        key: item.key,
        label: item.label || this.humanizeKey(item.key),
      }));
      return {
        rowId: row.id,
        columnKey: column.key,
        title,
        type,
        text: '',
        tableRows,
        tableColumns,
        chart: null,
        top,
        left,
        loading: false,
        error: '',
        requestId,
      };
    }

    if (type === 'chart') {
      const chart = this.normalizeChartConfig(normalized.chart || (this.hasChartPayload(source) ? source : null));
      return {
        rowId: row.id,
        columnKey: column.key,
        title,
        type,
        text: '',
        tableRows: [],
        tableColumns: [],
        chart,
        top,
        left,
        loading: false,
        error: '',
        requestId,
      };
    }

    return {
      rowId: row.id,
      columnKey: column.key,
      title,
      type: 'text',
      text: normalized.text || String(source ?? this.getDisplayValue(row.source, column)),
      tableRows: [],
      tableColumns: [],
      chart: null,
      top,
      left,
      loading: false,
      error: '',
      requestId,
    };
  }

  closePopup(): void {
    this.activePopup = null;
    this.cdr.markForCheck();
  }

  onPopupPanelClick(event: MouseEvent): void {
    event.stopPropagation();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.activePopup) return;
    const el = event.target as HTMLElement;
    if (!el?.closest('.super-table-popup') && !el?.closest('.popup-trigger')) {
      this.activePopup = null;
      this.cdr.markForCheck();
    }
  }

  @HostListener('document:keydown.escape')
  onEscapePress(): void {
    if (this.activePopup) {
      this.activePopup = null;
      this.cdr.markForCheck();
    }
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    this.queueStickyOffsetCalibration();
  }

  getPopupStyle(): Record<string, string> {
    if (!this.activePopup) return {};
    return { top: `${this.activePopup.top}px`, left: `${this.activePopup.left}px` };
  }
  onControlValueChange(control: SuperControl, event: Event): void {
    const value = (event.target as HTMLSelectElement).value;
    this.controlValues[control.key] = value;
    this.handleControlAction(control, value);
  }

  onControlClick(control: SuperControl, event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    const value = this.controlValues[control.key] ?? control.value ?? true;
    this.handleControlAction(control, value);
  }

  handleControlAction(control: SuperControl, value: any): void {
    if (control.filterKey) {
      this.globalFilterService.updateFilterSelection(
        control.filterKey,
        [value],
        control.asQueryParam === true,
        this.data?.widgetId,
        true,
        control.filterListener,
      );
    }

    if (control.navigate) this.navigate(control.navigate, { controlKey: control.key, value });

    this.emitCustomEvent(control.eventName || this.data?.controlEventName, {
      widgetId: this.data?.widgetId,
      controlKey: control.key,
      value,
      values: this.cloneObject(this.controlValues),
    });
  }

  onTableAction(action: SuperAction, event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
    if (!this.isActionVisible(action, null)) return;
    if (this.isTableActionBusy(action)) return;
    if (action.navigate) this.navigate(action.navigate, { value: action.value, widgetId: this.data?.widgetId });
    if (action.apiUrl) {
      this.executeTableActionApi(action);
      return;
    }
    this.emitTableActionEvent(action, undefined, undefined);
  }

  isTableActionBusy(action: SuperAction): boolean {
    const key = this.getTableActionLoadingKey(action);
    return this.tableActionLoading[key] === true;
  }

  getTableActionLoadingKey(action: SuperAction): string {
    return String(action.value ?? action.label ?? '');
  }

  emitTableActionEvent(action: SuperAction, response?: any, error?: any): void {
    this.emitCustomEvent(action.eventName || this.data?.actionEventName, {
      widgetId: this.data?.widgetId,
      action: action.value ?? action.label,
      rows: this.cloneRows(this.rawRows),
      controls: this.cloneObject(this.controlValues),
      filters: this.cloneObject(this.currentFilters),
      response: response !== undefined ? this.cloneObject(response) : undefined,
      error: error ? String(error) : undefined,
    });
  }

  executeTableActionApi(action: SuperAction): void {
    if (!action.apiUrl) return;
    const context = this.buildExternalContext(action);
    const url = this.resolveFilterTemplate(String(action.apiUrl), context);
    const requestConfig = this.buildTableActionRequestConfig(action);
    const filters = this.buildExternalFilters(
      action.queryParams,
      action.requestParams,
      this.currentFilters,
      action.useQueryParams === true || action.useQueryParam === true,
      action,
    );
    const method = action.method || this.resolveMethod(url);
    const key = this.getTableActionLoadingKey(action);
    this.tableActionLoading[key] = true;
    this.cdr.markForCheck();

    this.configService
      .getConfig(url, requestConfig, filters, method, true)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          this.tableActionLoading[key] = false;
          const extracted = this.resolveTableActionResponse(action, response);
          this.emitTableActionEvent(action, extracted, undefined);
          this.cdr.markForCheck();
        },
        error: (err: any) => {
          this.tableActionLoading[key] = false;
          this.emitTableActionEvent(action, undefined, err?.message || action.errorMessage || 'Action API call failed.');
          this.cdr.markForCheck();
        },
      });
  }

  buildTableActionRequestConfig(action: SuperAction): any {
    const payload = {
      widgetId: this.data?.widgetId,
      action: action.value ?? action.label,
      rows: this.cloneRows(this.rawRows),
      controlValues: this.cloneObject(this.controlValues),
      filters: this.cloneObject(this.currentFilters),
      customPayload: this.cloneObject(action.customPayload || {}),
    };

    const config: any = {
      ...this.cloneObject(action || {}),
      customPayload: payload.customPayload,
      skipDefaultPayload: action.skipDefaultPayload === true,
      widgetId: payload.widgetId,
      action: payload.action,
      requestData: payload,
    };

    [
      'label',
      'className',
      'eventName',
      'disabled',
      'disabledKey',
      'showWhenKey',
      'navigate',
      'apiUrl',
      'method',
      'queryParams',
      'requestParams',
      'useQueryParams',
      'useQueryParam',
      'responsePath',
      'dataPath',
      'successMessage',
      'errorMessage',
    ].forEach((key) => delete config[key]);

    return config;
  }

  resolveTableActionResponse(action: SuperAction, response: any): any {
    const paths = [action.responsePath, action.dataPath].filter(Boolean);
    for (const path of paths) {
      const value = this.getValueByPath(response, String(path));
      if (value !== undefined) return value;
    }
    return response;
  }

  getCellActions(row: any, column: RenderColumn): SuperAction[] {
    const cellCfg = this.getCellConfig(row, column.key);
    const actions = cellCfg?.actions || column.actions || [];
    return Array.isArray(actions) ? actions : [];
  }

  isActionVisible(action: SuperAction, row: any): boolean {
    if (!action?.showWhenKey) return true;
    return Boolean(this.getValueByPath(row, action.showWhenKey));
  }

  isActionDisabled(action: SuperAction, row: any): boolean {
    if (action.disabled) return true;
    if (action.disabledKey) return Boolean(this.getValueByPath(row, action.disabledKey));
    return false;
  }

  onCellAction(action: SuperAction, row: FlatRow, column: RenderColumn, event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
    if (this.isActionDisabled(action, row.source) || !this.isActionVisible(action, row.source)) return;
    if (action.navigate) this.navigate(action.navigate, row.source);

    this.emitCustomEvent(action.eventName || this.data?.actionEventName, {
      widgetId: this.data?.widgetId,
      rowId: row.id,
      row: this.cloneObject(row.source),
      columnKey: column.key,
      action: action.value ?? action.label,
    });
  }

  onCellNavigate(row: FlatRow, column: RenderColumn, event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
    const navigation = this.getResolvedCellConfig(row.source, column).navigation;
    if (!navigation) return;
    this.navigate(navigation, row.source);
  }

  navigate(navigation: SuperNavigation, row: any): void {
    const rawPath = navigation.url || navigation.path;
    if (!rawPath || typeof window === 'undefined') return;

    let path = String(rawPath);
    if (navigation.pathParams) {
      Object.keys(navigation.pathParams).forEach((param) => {
        const sourcePath = String(navigation.pathParams?.[param] || '');
        const value = sourcePath ? this.getValueByPath(row, sourcePath) : undefined;
        path = path.replace(`:${param}`, encodeURIComponent(String(value ?? '')));
      });
    }

    const query = new URLSearchParams();
    if (navigation.queryParams) {
      Object.keys(navigation.queryParams).forEach((key) => {
        const sourcePath = String(navigation.queryParams?.[key] || '');
        const value = sourcePath ? this.getValueByPath(row, sourcePath) : undefined;
        if (Array.isArray(value)) value.forEach((v) => query.append(key, String(v)));
        else if (value !== undefined && value !== null && value !== '') query.set(key, String(value));
      });
    }

    const url = query.toString() ? `${path}${path.includes('?') ? '&' : '?'}${query.toString()}` : path;
    if (navigation.openInNewTab !== false) {
      window.open(url, '_blank');
      return;
    }
    window.location.assign(url);
  }

  hasChartPayload(value: any): boolean {
    if (value === undefined || value === null) return false;
    if (Array.isArray(value)) return value.length > 0;
    if (typeof value !== 'object') return this.isNumeric(value);
    // Be conservative: row payloads can contain arbitrary objects (e.g. `details`),
    // so don't treat every object as a chart unless it looks chart-like.
    return (
      Array.isArray(value.series)
      || value.series !== undefined
      || value.data !== undefined
      || value.dataset !== undefined
      || value.option !== undefined
      || value.options !== undefined
      || value.chart !== undefined
      || value.chartType !== undefined
      || value.componentType !== undefined
      || value.type !== undefined
      || value.value !== undefined
      || value.max !== undefined
      || value.apiUrl !== undefined
      || value.widgetData !== undefined
    );
  }

  getConfiguredRowIdKey(): string {
    return String(this.data?.drillDown?.rowIdKey || 'id');
  }

  getConfiguredRowLabelKey(): string {
    return String(this.data?.drillDown?.toggleColumnKey || '');
  }

  getRowIdentifier(row: any): string {
    const idKey = this.getConfiguredRowIdKey();
    const idValue = this.getValueByPath(row, idKey);
    if (idValue !== undefined && idValue !== null && idValue !== '') return String(idValue);
    if (row?.id !== undefined && row?.id !== null && row?.id !== '') return String(row.id);
    const labelKey = this.getConfiguredRowLabelKey();
    if (labelKey) {
      const labelValue = this.getValueByPath(row, labelKey);
      if (labelValue !== undefined && labelValue !== null && labelValue !== '') return String(labelValue);
    }
    return 'row';
  }

  resolveChartConfig(row: any, column: RenderColumn): SuperChart | null {
    const cellCfg = this.getCellConfig(row, column.key);
    const configuredChart = this.cloneObject(cellCfg?.chart || column.chart || {});
    const rawValue = row?.[column.key];

    const resolvedType = this.resolveChartType(row, configuredChart, rawValue);
    const payload = this.resolveChartPayload(row, configuredChart, rawValue);
    const typeConfig = this.resolveTypeConfig(row, configuredChart, resolvedType);
    const base = this.stripChartMetaConfig(configuredChart);

    if (resolvedType && !this.isInlineMiniChartType(resolvedType)) {
      const widgetData = this.buildDynamicWidgetConfig(base, typeConfig, payload, rawValue);
      const dynamicChart: any = { type: resolvedType, data: widgetData };
      return this.normalizeChartConfig(dynamicChart);
    }

    let merged: any = {};
    if (base && typeof base === 'object' && !Array.isArray(base)) merged = { ...base };
    if (typeConfig && typeof typeConfig === 'object' && !Array.isArray(typeConfig)) {
      merged = { ...merged, ...this.cloneObject(typeConfig) };
    }

    const effectivePayload = payload !== undefined ? payload : rawValue;
    if (effectivePayload !== undefined) {
      if (Array.isArray(effectivePayload)) merged.series = this.cloneObject(effectivePayload);
      else if (effectivePayload && typeof effectivePayload === 'object') merged = { ...merged, ...this.cloneObject(effectivePayload) };
      else if (effectivePayload !== null) merged.value = effectivePayload;
    }

    if (resolvedType) {
      merged.type = resolvedType;
    } else if (!merged.type && (merged.componentType || merged.chartType)) {
      merged.type = merged.componentType || merged.chartType;
    }

    if (!merged || Object.keys(merged).length === 0) {
      return null;
    }
    return this.normalizeChartConfig(merged);
  }

  resolveTypeConfig(row: any, chartConfig: any, resolvedType: string): any {
    if (!resolvedType) return null;

    const directMaps = [chartConfig?.typeConfigMap, chartConfig?.chartConfigMap, chartConfig?.widgetConfigMap].filter(Boolean);
    for (const map of directMaps) {
      if (map && typeof map === 'object' && !Array.isArray(map)) {
        if (map[resolvedType] !== undefined) return this.cloneObject(map[resolvedType]);
        if (map['*'] !== undefined) return this.cloneObject(map['*']);
      }
    }

    const pathCandidates = [chartConfig?.typeConfigPath, chartConfig?.chartConfigPath, chartConfig?.widgetConfigPath].filter(Boolean);
    for (const path of pathCandidates) {
      const map = this.getValueByPath(row, String(path));
      if (!map || typeof map !== 'object' || Array.isArray(map)) continue;
      if (map[resolvedType] !== undefined) return this.cloneObject(map[resolvedType]);
      if (map['*'] !== undefined) return this.cloneObject(map['*']);
    }
    return null;
  }

  resolveChartType(row: any, chartConfig: any, rawValue: any): string {
    const explicitType = this.normalizeDynamicType(chartConfig?.type || chartConfig?.componentType || chartConfig?.chartType);
    if (explicitType) return explicitType;

    const pathCandidates = [chartConfig?.typePath, chartConfig?.componentTypePath, chartConfig?.chartTypePath].filter(Boolean);
    for (const path of pathCandidates) {
      const fromPath = this.getValueByPath(row, String(path));
      const value = this.normalizeDynamicType(fromPath);
      if (value) return value;
    }

    const typeMap = chartConfig?.typeMap;
    if (typeMap && typeof typeMap === 'object' && !Array.isArray(typeMap)) {
      const sourcePath = String(
        chartConfig?.typeSourcePath || chartConfig?.typeMapPath || chartConfig?.typeKeyPath || chartConfig?.typePath || 'id',
      );
      const sourceValue = this.getValueByPath(row, sourcePath);
      if (sourceValue !== undefined && sourceValue !== null) {
        const mapped = typeMap[String(sourceValue)];
        const mappedValue = this.normalizeDynamicType(mapped);
        if (mappedValue) return mappedValue;
      }
      const wildcardMapped = this.normalizeDynamicType(typeMap['*']);
      if (wildcardMapped) return wildcardMapped;
    }

    const rawType = this.normalizeDynamicType(rawValue?.type || rawValue?.componentType || rawValue?.chartType);
    if (rawType) return rawType;

    const inferredFromConfig = this.inferSingleTypeFromConfigMap(chartConfig);
    if (inferredFromConfig) return inferredFromConfig;

    const defaultType = this.normalizeDynamicType(chartConfig?.defaultType);
    return defaultType;
  }

  inferSingleTypeFromConfigMap(chartConfig: any): string {
    const mapCandidates = [chartConfig?.typeConfigMap, chartConfig?.chartConfigMap, chartConfig?.widgetConfigMap].filter(Boolean);
    for (const map of mapCandidates) {
      if (!map || typeof map !== 'object' || Array.isArray(map)) continue;
      const keys = Object.keys(map).filter((k) => k && k !== '*');
      if (keys.length === 1) return this.normalizeDynamicType(keys[0]);
    }
    return '';
  }

  normalizeDynamicType(type: any): string {
    const raw = String(type ?? '').trim().toLowerCase();
    if (!raw) return '';

    const normalized = raw
      .replace(/[_\s]+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '');

    const aliases: Record<string, string> = {
      bar: 'bar-chart',
      barchart: 'bar-chart',
      'bar-chart': 'bar-chart',
      line: 'line-chart',
      linechart: 'line-chart',
      'line-chart': 'line-chart',
      pie: 'pie-chart',
      piechart: 'pie-chart',
      'pie-chart': 'pie-chart',
      donut: 'donut-chart',
      donutchart: 'donut-chart',
      'donut-chart': 'donut-chart',
      waterfall: 'waterfall-chart',
      waterfallchart: 'waterfall-chart',
      'waterfall-chart': 'waterfall-chart',
      'water-fall-chart': 'waterfall-chart',
      stackbar: 'stack-bar-chart',
      'stack-bar': 'stack-bar-chart',
      'stacked-bar': 'stack-bar-chart',
      'stack-bar-chart': 'stack-bar-chart',
      'stacked-bar-chart': 'stack-bar-chart',
      progressbar: 'progress',
      'progress-bar': 'progress',
      progress: 'progress',
      supertable: 'config-driven-super-table',
      'super-table': 'config-driven-super-table',
      'unified-super-table': 'config-driven-super-table',
      'config-driven-super-table': 'config-driven-super-table',
      'dynamic-data-grid': 'config-driven-super-table',
    };

    return aliases[normalized] || normalized;
  }

  buildDynamicWidgetConfig(base: any, typeConfig: any, payload: any, rawValue: any): any {
    let widgetData: any = {};
    if (base && typeof base === 'object' && !Array.isArray(base)) widgetData = { ...widgetData, ...this.cloneObject(base) };
    if (typeConfig && typeof typeConfig === 'object' && !Array.isArray(typeConfig)) widgetData = { ...widgetData, ...this.cloneObject(typeConfig) };

    delete widgetData.type;
    delete widgetData.componentType;
    delete widgetData.chartType;
    delete widgetData.widgetData;

    const effectivePayload = payload !== undefined ? payload : rawValue;
    if (effectivePayload !== undefined) {
      widgetData.data = this.cloneObject(effectivePayload);
    } else if (widgetData.data !== undefined) {
      widgetData.data = this.cloneObject(widgetData.data);
    }

    return widgetData;
  }

  resolveChartPayload(row: any, chartConfig: any, rawValue: any): any {
    const configuredPaths = [
      chartConfig?.dataPath,
      chartConfig?.payloadPath,
      chartConfig?.widgetDataPath,
      chartConfig?.chartDataPath,
    ].filter(Boolean);

    for (const path of configuredPaths) {
      const data = this.getValueByPath(row, String(path));
      if (data !== undefined) return this.cloneObject(data);
    }

    if (rawValue && typeof rawValue === 'object' && !Array.isArray(rawValue)) {
      const rawType = rawValue.type || rawValue.componentType || rawValue.chartType;
      if (!rawType && rawValue.data !== undefined) {
        return this.cloneObject(rawValue.data);
      }
    }

    if (rawValue !== undefined) return this.cloneObject(rawValue);
    return undefined;
  }

  stripChartMetaConfig(chartConfig: any): any {
    if (!chartConfig || typeof chartConfig !== 'object' || Array.isArray(chartConfig)) return chartConfig;
    const cleaned = this.cloneObject(chartConfig);
    [
      'typePath',
      'componentTypePath',
      'chartTypePath',
      'typeSourcePath',
      'typeMapPath',
      'typeKeyPath',
      'typeMap',
      'defaultType',
      'dataPath',
      'payloadPath',
      'widgetDataPath',
      'chartDataPath',
      'typeConfigMap',
      'chartConfigMap',
      'widgetConfigMap',
      'typeConfigPath',
      'chartConfigPath',
      'widgetConfigPath',
    ].forEach((key) => delete cleaned[key]);
    return cleaned;
  }

  isInlineMiniChart(chart: SuperChart | null): boolean {
    return this.isInlineMiniChartType(chart?.componentType || chart?.chartType || chart?.type);
  }

  isInlineMiniChartType(type: string | undefined): boolean {
    const normalized = String(type || '').toLowerCase();
    return normalized === 'progress';
  }

  getCellDynamicChartTarget(row: FlatRow, column: RenderColumn, chart: SuperChart): DynamicChartTarget | null {
    return this.buildDynamicChartTarget(chart, row.source, column, false);
  }

  getPopupDynamicChartTarget(chart: SuperChart): DynamicChartTarget | null {
    if (!this.activePopup) return null;
    const row = this.flatRows.find((item) => item.id === this.activePopup?.rowId);
    const column = this.visibleColumns.find((item) => item.key === this.activePopup?.columnKey);
    if (!row || !column) return null;
    return this.buildDynamicChartTarget(chart, row.source, column, true);
  }

  queueStickyOffsetCalibration(): void {
    if (this.stickyOffsetScheduled) return;
    this.stickyOffsetScheduled = true;
    const run = () => {
      this.stickyOffsetScheduled = false;
      this.calibrateStickyOffsetsFromDom();
      // Run one more pass after layout settles (zoom/font/render rounding).
      setTimeout(() => this.calibrateStickyOffsetsFromDom(), 0);
    };

    if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
      window.requestAnimationFrame(() => run());
      return;
    }
    setTimeout(() => run(), 0);
  }

  calibrateStickyOffsetsFromDom(): void {
    const table = this.tableRef?.nativeElement;
    if (!table || !this.visibleColumns.length) return;

    const verticalOffsetsChanged = this.calibrateStickyTopOffsetsFromDom(table);
    const referenceCells = this.resolveReferenceCellsForStickyCalibration(table);
    if (referenceCells.length === 0) {
      if (verticalOffsetsChanged) {
        this.cdr.markForCheck();
      }
      return;
    }

    let nextLeft = 0;
    let leftOffsetsChanged = false;

    this.visibleColumns.forEach((column, index) => {
      if (!this.isFrozenColumn(index, column)) return;
      const snappedLeft = this.snapStickyCoordinate(nextLeft);
      if (Math.abs(column.leftOffset - snappedLeft) > 0.01) {
        column.leftOffset = snappedLeft;
        leftOffsetsChanged = true;
      }

      const measuredWidth = this.getMeasuredElementWidth(referenceCells[index], column.widthPx);
      nextLeft = this.snapStickyCoordinate(nextLeft + measuredWidth);
    });

    if (leftOffsetsChanged) {
      this.headerRows = this.buildHeaderRows(this.visibleColumns);
    }

    if (leftOffsetsChanged || verticalOffsetsChanged) {
      this.cdr.markForCheck();
    }
  }

  calibrateStickyTopOffsetsFromDom(table: HTMLTableElement): boolean {
    const nextHeaderHeights = this.freezeHeader
      ? this.measureTableRowHeights(Array.from(table.querySelectorAll('thead tr')), this.headerRows.length, this.headerRowHeight)
      : [];
    const nextFrozenRowHeights = this.freezeTopRows > 0
      ? this.measureTableRowHeights(Array.from(table.querySelectorAll('tbody tr')), this.freezeTopRows, this.frozenTopRowHeight || this.rowHeight)
      : [];

    const changed =
      !this.areNumberArraysClose(this.measuredHeaderRowHeights, nextHeaderHeights)
      || !this.areNumberArraysClose(this.measuredFrozenRowHeights, nextFrozenRowHeights);

    if (changed) {
      this.measuredHeaderRowHeights = nextHeaderHeights;
      this.measuredFrozenRowHeights = nextFrozenRowHeights;
    }

    return changed;
  }

  resolveReferenceCellsForStickyCalibration(table: HTMLTableElement): HTMLElement[] {
    const firstBodyRow = table.querySelector('tbody tr') as HTMLTableRowElement | null;
    if (firstBodyRow?.children?.length) {
      return Array.from(firstBodyRow.children).map((cell) => cell as HTMLElement);
    }

    const headerRows = table.querySelectorAll('thead tr');
    if (headerRows.length > 0) {
      const leafRow = headerRows[headerRows.length - 1] as HTMLTableRowElement;
      return Array.from(leafRow.children).map((cell) => cell as HTMLElement);
    }

    return [];
  }

  getMeasuredElementWidth(cell: HTMLElement | undefined, fallback: number): number {
    if (!cell) return fallback;
    const rectWidth = cell.getBoundingClientRect().width;
    if (Number.isFinite(rectWidth) && rectWidth > 0) {
      return this.snapStickyCoordinate(rectWidth);
    }
    const width = cell.offsetWidth;
    return width > 0 ? this.snapStickyCoordinate(width) : fallback;
  }

  measureTableRowHeights(rows: HTMLElement[], count: number, fallback: number): number[] {
    const heights: number[] = [];
    const limit = Math.max(0, count);
    for (let index = 0; index < limit; index += 1) {
      heights.push(this.getMeasuredElementHeight(rows[index], fallback));
    }
    return heights;
  }

  getMeasuredElementHeight(row: HTMLElement | undefined, fallback: number): number {
    if (!row) return fallback;
    const rectHeight = row.getBoundingClientRect().height;
    if (Number.isFinite(rectHeight) && rectHeight > 0) {
      return this.snapStickyCoordinate(rectHeight);
    }
    const height = row.offsetHeight;
    return height > 0 ? this.snapStickyCoordinate(height) : fallback;
  }

  sumMeasuredHeights(heights: number[], count: number, fallback: number): number {
    const limit = Math.max(0, count);
    let total = 0;
    for (let index = 0; index < limit; index += 1) {
      const measured = heights[index];
      total += Number.isFinite(measured) && measured > 0 ? measured : fallback;
    }
    return this.snapStickyCoordinate(total);
  }

  areNumberArraysClose(left: number[], right: number[]): boolean {
    if (left.length !== right.length) return false;
    return left.every((value, index) => Math.abs(value - right[index]) <= 0.5);
  }

  snapStickyCoordinate(value: number): number {
    if (!Number.isFinite(value) || value <= 0) return 0;
    const devicePixelRatio =
      typeof window !== 'undefined' && Number.isFinite(window.devicePixelRatio) && window.devicePixelRatio > 0
        ? window.devicePixelRatio
        : 1;
    return Math.round(value * devicePixelRatio) / devicePixelRatio;
  }

  buildDynamicChartTarget(
    chart: SuperChart,
    row: any,
    column: RenderColumn,
    isPopup: boolean,
  ): DynamicChartTarget | null {
    const dynamicType = this.normalizeDynamicType(chart.componentType || chart.chartType || chart.type);
    if (!dynamicType || this.isInlineMiniChartType(dynamicType)) {
      return null;
    }

    let widgetData: any;
    if (chart.data && typeof chart.data === 'object') {
      widgetData = this.cloneObject(chart.data);
    } else if (chart.widgetData && typeof chart.widgetData === 'object') {
      widgetData = this.cloneObject(chart.widgetData);
    } else {
      widgetData = this.cloneObject(chart);
    }

    widgetData.type = widgetData.type || dynamicType;
    widgetData.widgetId =
      widgetData.widgetId
      || `${this.data?.widgetId || 'config-driven-super-table'}-${isPopup ? 'popup' : 'cell'}-${this.getRowIdentifier(row)}-${column.key}`;

    if (!widgetData.height) {
      widgetData.height = isPopup ? '220px' : `${Math.max(this.getChartHeight(chart, 84), 84)}px`;
    }

    return {
      type: dynamicType,
      data: widgetData,
    };
  }

  normalizeChartConfig(raw: any): SuperChart | null {
    if (!raw) return null;
    const explicitType = this.normalizeDynamicType(raw.componentType || raw.chartType || raw.type);
    const hasInlineSignal =
      raw.value !== undefined || raw.max !== undefined || Array.isArray(raw.series) || Array.isArray(raw.data);
    const type = explicitType || (hasInlineSignal ? 'bar-chart' : '');
    if (!type) return null;

    if (!this.isInlineMiniChartType(type)) {
      return {
        ...this.cloneObject(raw),
        type,
      };
    }

    const series = (Array.isArray(raw.series) ? raw.series : Array.isArray(raw.data) ? raw.data : [])
      .map((v: any) => Number(v))
      .filter((v: number) => Number.isFinite(v));

    const chart: SuperChart = {
      type,
      componentType: raw.componentType,
      chartType: raw.chartType,
      data: raw.data,
      widgetData: raw.widgetData,
      series,
      value: raw.value !== undefined ? Number(raw.value) : undefined,
      max: raw.max !== undefined ? Number(raw.max) : undefined,
      color: raw.color,
      lineColor: raw.lineColor,
      barColor: raw.barColor,
      height: raw.height,
    };

    if (!chart.series?.length && chart.value === undefined) return null;
    return chart;
  }

  getChartType(chart: SuperChart | null): string {
    return String(chart?.type || '').toLowerCase();
  }

  getProgressColor(chart: SuperChart | null): string {
    return chart?.color || '#0f766e';
  }

  getChartHeight(chart: SuperChart | null, fallback = 44): number {
    const h = Number(chart?.height);
    return Number.isFinite(h) && h > 0 ? h : fallback;
  }

  getProgressPercent(chart: SuperChart | null): number {
    if (!chart) return 0;
    const value = Number(chart.value ?? chart.series?.[0] ?? 0);
    const max = Number(chart.max ?? 100);
    if (!Number.isFinite(value) || !Number.isFinite(max) || max <= 0) return 0;
    return Math.max(0, Math.min(100, (value / max) * 100));
  }

  getRowConfig(row: any): any {
    return row?.[this.getRowConfigKey()] || {};
  }

  getCellConfig(row: any, columnKey: string): any {
    return row?.[this.getCellConfigKey()]?.[columnKey] || {};
  }

  getRowConfigKey(): string {
    return this.data?.rowConfigKey || '_rowConfig';
  }

  getCellConfigKey(): string {
    return this.data?.cellConfigKey || '_cellConfig';
  }

  getReservedKeys(): string[] {
    return [this.getRowConfigKey(), this.getCellConfigKey()];
  }

  handleCellClick(row: FlatRow, column: RenderColumn): void {
    this.emitCustomEvent(this.data?.actionEventName, {
      widgetId: this.data?.widgetId,
      action: 'cellClick',
      rowId: row.id,
      row: this.cloneObject(row.source),
      columnKey: column.key,
      value: this.getCellValue(row.source, column),
    });
  }

  emitCustomEvent(eventName: string | undefined, payload: any): void {
    if (!eventName || typeof window === 'undefined') return;
    const ns = this.data?.eventNamespace;
    const finalName = ns ? `${ns}:${eventName}` : eventName;
    window.dispatchEvent(new CustomEvent(finalName, { detail: payload }));
  }

  usesRemoteData(): boolean {
    return Boolean(this.data?.apiUrl || this.data?.mockDataUrl);
  }

  resolveMethod(url: string): string {
    if (this.data?.method) return this.data.method;
    return String(url).endsWith('.json') ? 'get' : 'post';
  }

  buildTableColumnsFromRows(rows: any[]): Array<{ key: string; label?: string }> {
    const first = Array.isArray(rows) && rows.length > 0 ? rows[0] : null;
    if (!first || typeof first !== 'object') return [];
    return Object.keys(first).map((key) => ({ key, label: this.humanizeKey(key) }));
  }

  getPopupChart(): SuperChart | null {
    return this.activePopup?.chart || null;
  }

  normalizeCssSize(value: string | number): string {
    return typeof value === 'number' ? `${value}px` : String(value);
  }

  toPixels(value: string | number | undefined, fallback: number): number {
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (/^\d+(\.\d+)?$/.test(trimmed)) return Number(trimmed);
      const px = trimmed.match(/^(\d+(\.\d+)?)px$/);
      if (px) return Number(px[1]);
    }
    return fallback;
  }

  isNumeric(value: any): boolean {
    return value !== null && value !== '' && !Number.isNaN(Number(value));
  }

  isDateLike(value: any): boolean {
    if (value instanceof Date) return true;
    if (typeof value !== 'string') return false;
    return !Number.isNaN(Date.parse(value));
  }

  areValuesEqual(a: any, b: any): boolean {
    return a === b || JSON.stringify(a) === JSON.stringify(b);
  }

  getValueByPath(source: any, path: string | undefined): any {
    if (!path) return undefined;
    return String(path)
      .split('.')
      .reduce((value: any, part: string) => (value == null ? undefined : value[part]), source);
  }

  humanizeKey(value: string): string {
    return String(value)
      .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
      .replace(/[_-]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .replace(/\b\w/g, (m) => m.toUpperCase());
  }

  cloneRows(rows: any[]): any[] {
    return rows.map((row) => this.cloneObject(row));
  }

  cloneObject<T>(value: T): T {
    if (Array.isArray(value)) return value.map((item) => this.cloneObject(item)) as T;
    if (value && typeof value === 'object') {
      return Object.keys(value as Record<string, any>).reduce((acc: any, key: string) => {
        acc[key] = this.cloneObject((value as Record<string, any>)[key]);
        return acc;
      }, {}) as T;
    }
    return value;
  }

  getColumnLeftOffset(index: number): number {
    return this.visibleColumns[index]?.leftOffset || 0;
  }

  getFrozenRowTopOffset(rowIndex: number): number {
    return this.getHeaderStackHeight() + this.getFrozenBodyRowsHeight(rowIndex);
  }
}

